import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc, addDoc, collection, serverTimestamp, query, where, getDocs, updateDoc, orderBy, limit } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuth } from '../utils/firebase';
import { 
  cacheGameData, 
  cacheGameState, 
  updateHostStage, 
  clearGameCache,
  getCachedGameState
} from '../utils/gameSync';
import { isMobileDevice } from '../utils/mobileRestrictions';
import MobileRestriction from '../components/MobileRestriction';
import HostLayout from '../layouts/HostLayout';
import { v4 as uuidv4 } from 'uuid';
import QRCode from 'qrcode';

// Host Mode Protection System
import { useHostModeProtection } from '../hooks/useHostModeProtection';
import { ResumeGameModal, LeaveConfirmationModal, SaveAndExitModal, StatusBanner } from '../components/HostModeModals';
import HostErrorBoundary from '../components/HostErrorBoundary';

const stageMap = {
  1: 'Pre-Game Slides (Auto)',
  2: 'Quarter 1 Categories',
  3: 'Quarter 1, Question 1',
  4: 'Quarter 1, Question 2',
  5: 'Quarter 1, Question 3',
  6: 'Quarter 1, Question 4',
  7: 'Quarter 1, Question 5',
  8: 'Quarter 2 Categories',
  9: 'Quarter 2, Question 1',
  10: 'Quarter 2, Question 2',
  11: 'Quarter 2, Question 3',
  12: 'Quarter 2, Question 4',
  13: 'Quarter 2, Question 5',
  14: 'Halftime Rules (Manual)',
  15: 'Halftime Category',
  16: 'Halftime Question',
  17: 'Halftime Ads (Manual)',
  18: 'Halftime Score Update (Manual)',
  19: 'Quarter 3 Categories',
  20: 'Quarter 3, Question 1',
  21: 'Quarter 3, Question 2',
  22: 'Quarter 3, Question 3',
  23: 'Quarter 3, Question 4',
  24: 'Quarter 3, Question 5',
  25: 'Quarter 4 Categories',
  26: 'Quarter 4, Question 1',
  27: 'Quarter 4, Question 2',
  28: 'Quarter 4, Question 3',
  29: 'Quarter 4, Question 4',
  30: 'Quarter 4, Question 5',
  31: 'Pre-Final Ads (Manual)',
  32: 'Pre-Final Score Update (Manual)',
  33: 'Pre-Final Intro Slides (Manual)',
  34: 'Final Category',
  35: 'Final Question',
  36: 'Tie Detection (Auto)',
  37: 'Tiebreaker Info Slides (Manual)',
  38: 'Tie Breaker Question',
  39: 'Final Score Summary (Manual)',
  40: 'Game Over'
};

const LaunchGame = () => {
  const { id: gameId } = useParams();
  const navigate = useNavigate();
  const { state, search } = useLocation();
  const sessionIdFromNav = state?.session?.sessionId;
  const { user } = useAuth();
  const [showMobileRestriction, setShowMobileRestriction] = useState(false);
  
  // Parse URL parameters for contract-compliant session loading
  const params = new URLSearchParams(search);
  const sessionId = params.get('session');
  const force = params.get('force') === '1';
  const skipResume = params.get('skipResume') === '1';
  
  // Session ready state to prevent redirect loops
  const [sessionReady, setSessionReady] = useState(false);
  const sessionReadyRef = useRef(false);
  const loadedOnceRef = useRef(false);
  const [gameLoaded, setGameLoaded] = useState(false);
  const [session, setSession] = useState(null);
  
  // Check for mobile device on mount
  useEffect(() => {
    if (isMobileDevice()) {
      setShowMobileRestriction(true);
    }
  }, []);

  // Timer helper functions - must be defined before useSessionCountdown
  const msToHMS = (ms) => {
    if (ms <= 0) return "00:00:00";
    const s = Math.floor(ms / 1000);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(h)}:${pad(m)}:${pad(sec)}`;
  };

  // Session countdown hook - must be defined before use
  const useSessionCountdown = (expiresAt) => {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
      if (!expiresAt) return;
      const id = setInterval(() => setNow(Date.now()), 1000);
      return () => clearInterval(id);
    }, [expiresAt]);
    
    // Handle both Firestore Timestamp and number formats
    const getExpiresAtMs = () => {
      if (!expiresAt) return 0;
      if (typeof expiresAt === 'number') return expiresAt;
      if (expiresAt.toMillis) return expiresAt.toMillis();
      return 0;
    };
    
    const remainingMs = getExpiresAtMs() - now;
    return { remainingMs, hms: msToHMS(remainingMs), expired: remainingMs <= 0 };
  };

  // Timer logic - must be at top level to avoid conditional hook calls
  const expiresAt = session?.expiresAt ?? null;
  const finalSubmitted = session?.finalScoresSubmitted || session?.finalized || session?.status === "completed";
  const showTimer = !!sessionReady && session?.status === "active" && !!expiresAt && !finalSubmitted;
  const { remainingMs, hms, expired } = useSessionCountdown(expiresAt);

  // Null-safe helper functions
  const getGameTitle = (g) => g?.title ?? g?.name ?? g?.metadata?.title ?? 'Untitled Game';
  const getGameRounds = (g) => g?.rounds ?? [];
  const getGameMode = (g) => g?.mode ?? 'standard';


  const [teams, setTeams] = useState([]);
  const [currentStage, setCurrentStage] = useState(1);
  const [gameData, setGameData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gameComplete, setGameComplete] = useState(false);
  const [hostPeekAnswer, setHostPeekAnswer] = useState(false);
  const [revealAnswer, setRevealAnswer] = useState(false);
  const [showRatingQR, setShowRatingQR] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [showSubmitConfirmation, setShowSubmitConfirmation] = useState(false);
  // const [gameExpired, setGameExpired] = useState(false); // Removed unused variable
  // const [timeRemaining, setTimeRemaining] = useState(null); // Removed unused variable
  const [slides, setSlides] = useState([]);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  
  // Host Mode Protection System
  const gameState = useMemo(() => {
    const state = {
      teams,
      currentStage,
      revealAnswer,
      gameComplete,
      currentSlideIndex,
      gameData: gameData ? { id: gameData.id, title: gameData.title } : null
    };
    console.log('🎮 GAME STATE UPDATE:', state);
    return state;
  }, [teams, currentStage, revealAnswer, gameComplete, currentSlideIndex, gameData]);

  const isGameActive = !loading && !error && gameData && !gameComplete;

  const protection = useHostModeProtection({
    gameId,
    gameState,
    isGameActive,
    autoSaveInterval: 5000, // Save every 5 seconds for better coverage
    onResume: () => {
      // Resume game from cached save data (protected from overwrites)
      console.log('🔄 RESUME CLICKED: Using cached save data for', gameId);
      
      const savedState = protection.cachedSaveData || getCachedGameState(gameId);
      
      console.log('🔄 SINGLE-SAVE RESUME: Restoring from cached save', { 
        gameId, 
        hasSavedState: !!savedState,
        teamsCount: savedState?.teams?.length || 0,
        stage: savedState?.currentStage || 1,
        usingCachedData: !!protection.cachedSaveData,
        fullSavedState: savedState
      });
      
      if (savedState) {
        console.log('📥 RESTORING STATE:', {
          teams: savedState.teams || [],
          currentStage: savedState.currentStage || 1,
          revealAnswer: savedState.revealAnswer || false,
          gameComplete: savedState.gameComplete || false,
          currentSlideIndex: savedState.currentSlideIndex || 0,
          fullSavedData: savedState
        });
        
        // 🚨 DEBUG: Check team structure in detail
        if (savedState.teams && savedState.teams.length > 0) {
          savedState.teams.forEach((team, index) => {
            console.log(`🧪 TEAM ${index + 1} STRUCTURE:`, {
              name: team.name,
              score: team.score,
              hasUsedPoints: !!team.usedPoints,
              hasCorrectAnswers: !!team.correctAnswers,
              hasWrongAnswers: !!team.wrongAnswers,
              usedPointsKeys: team.usedPoints ? Object.keys(team.usedPoints) : [],
              correctAnswersKeys: team.correctAnswers ? Object.keys(team.correctAnswers) : [],
              wrongAnswersKeys: team.wrongAnswers ? Object.keys(team.wrongAnswers) : [],
              usedPointsData: team.usedPoints,
              correctAnswersData: team.correctAnswers,
              wrongAnswersData: team.wrongAnswers
            });
          });
        }
        
        // Ensure all teams have required properties for score breakdown (backup safety)
        const restoredTeams = (savedState.teams || []).map(team => ({
          ...team,
          usedPoints: team.usedPoints || {},
          correctAnswers: team.correctAnswers || {},
          wrongAnswers: team.wrongAnswers || {}
        }));
        
        setTeams(restoredTeams);
        setCurrentStage(savedState.currentStage || 1);
        setRevealAnswer(savedState.revealAnswer || false);
        setGameComplete(savedState.gameComplete || false);
        setCurrentSlideIndex(savedState.currentSlideIndex || 0);
        console.log('✅ RESUMED: Game state restored from save');
      } else {
        console.error('❌ RESUME FAILED: No saved state found');
      }
    },
    onDiscard: () => {
      // Clear the single save and start fresh
      clearGameCache(gameId);
      console.log('🗑️ SINGLE-SAVE DISCARDED: Starting fresh game');
    }
  });

  // Exit game handler - now uses the save-and-exit flow
  const handleExitGame = () => {
    // Always use the protection's exit handler which shows save confirmation
    protection.handleExitGame();
  };
  
  // Slide navigation functions
  const nextSlide = () => {
    if (currentSlideIndex < getSlidesForCurrentStage.length - 1) {
      setCurrentSlideIndex(prev => prev + 1);
    } else {
      // Move to next stage when all slides are shown
      handleStageChange(getNextStage(currentStage));
      setCurrentSlideIndex(0);
    }
  };

  const previousSlide = () => {
    if (currentSlideIndex > 0) {
      setCurrentSlideIndex(prev => prev - 1);
    } else {
      // Move to previous stage
      handleStageChange(getPreviousStage(currentStage));
      // Reset slide index for the new stage
      setCurrentSlideIndex(0);
    }
  };

  const isSlideStage = (stage) => {
    return [1, 14, 17, 31, 33, 37].includes(stage);
  };

  // Helper function to get the next stage
  const getNextStage = (currentStage) => {
    return Math.min(40, currentStage + 1);
  };

  // Helper function to get the previous stage
  const getPreviousStage = (currentStage) => {
    return Math.max(1, currentStage - 1);
  };
  
  // Get slides for the current stage (memoized to prevent infinite re-renders)
  const getSlidesForCurrentStage = useMemo(() => {
    const stageMap = {
      1: { stage: 'pregame', category: null },           // Stage 1 = pregame (all categories)
      14: { stage: 'halftime', category: 'gameInfo' },   // Stage 14 = halftime rules
      17: { stage: 'halftime', category: 'ads' },        // Stage 17 = halftime ads
      31: { stage: 'final', category: 'ads' },           // Stage 31 = pre-final ads
      33: { stage: 'final', category: 'gameInfo' },      // Stage 33 = pre-final intro slides
      37: { stage: 'tiebreaker', category: null }        // Stage 37 = tiebreaker info slides (all categories)
    };
    
    const stageConfig = stageMap[currentStage] || { stage: 'pregame', category: null };
    let stageSlides;
    
    if (stageConfig.category) {
      // Filter by both stage and category
      stageSlides = slides.filter(slide => 
        slide.stage === stageConfig.stage && slide.category === stageConfig.category
      );
    } else {
      // Filter by stage only (for stages that show all categories)
      stageSlides = slides.filter(slide => slide.stage === stageConfig.stage);
    }
    
    // Only log once when slides change, not on every render
    if (process.env.NODE_ENV === 'development') {
      console.log(`📢 Stage ${currentStage} (${stageConfig.stage}${stageConfig.category ? '/' + stageConfig.category : ''}) slides:`, stageSlides.length);
    }
    
    return stageSlides;
  }, [currentStage, slides]);
  const [showTeamBreakdown, setShowTeamBreakdown] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [scoreAnnouncementIndex, setScoreAnnouncementIndex] = useState(-1); // -1 = not announcing, 0+ = team index
  const [tiedTeams, setTiedTeams] = useState([]); // Teams participating in tiebreaker
  const [tieGroups, setTieGroups] = useState([]); // Groups of tied teams by position
  const [teamsAnsweredCurrentQuestion, setTeamsAnsweredCurrentQuestion] = useState(new Set()); // Track which teams have answered current question
  const [editingTeamName, setEditingTeamName] = useState(false);
  const [editingTeamNameValue, setEditingTeamNameValue] = useState('');
  const [showManualEntryModal, setShowManualEntryModal] = useState(false);
  const [manualTeamName, setManualTeamName] = useState('');
  const wasFullscreenBeforeModalRef = useRef(false); // Track fullscreen state before modal
  const [editingQuestion, setEditingQuestion] = useState(null); // {quarterNumber, questionIndex}
  const [editingQuestionPoints, setEditingQuestionPoints] = useState(0);
  const [editingQuestionStatus, setEditingQuestionStatus] = useState(''); // 'correct', 'wrong', 'none'

  // League team search state
  const [showTeamSearchModal, setShowTeamSearchModal] = useState(false);
  const [teamSearchQuery, setTeamSearchQuery] = useState('');
  const [leagueTeams, setLeagueTeams] = useState([]);
  const [filteredLeagueTeams, setFilteredLeagueTeams] = useState([]);
  const [recentTeams, setRecentTeams] = useState([]);

  // 12-hour launch warning modal state
  const [showLaunchWarning, setShowLaunchWarning] = useState(false);
  const [launchSession, setLaunchSession] = useState(null);

  // Only log once on mount, not on every render
  useEffect(() => {
    console.log('🚀 LaunchGame component loaded with gameId:', gameId);
  }, [gameId]);

  // Load game document separately from session
  useEffect(() => {
    let cancelled = false;

    const loadGameDoc = async () => {
      if (!gameId) return;
      
      try {
        console.log('🧩 Loading game document:', gameId);
        let gameDoc = null;
        let data = null;

        // Try regular games collection first (personal games)
        try {
          const gameRef = doc(db, 'games', gameId);
          const snap = await getDoc(gameRef);
          if (snap.exists()) {
            data = snap.data();
            gameDoc = { id: snap.id, ...data };
            console.log('🧩 Found game in regular games collection');
          }
        } catch (error) {
          console.log('🧩 Game not found in regular games collection');
        }

        // Try marketplace games collection
        if (!gameDoc) {
          try {
            const marketplaceRef = doc(db, 'games', 'content', 'triviaGames', gameId);
            const snap = await getDoc(marketplaceRef);
            if (snap.exists()) {
              data = snap.data();
              gameDoc = { id: snap.id, ...data };
              console.log('🧩 Found game in marketplace collection');
            }
          } catch (error) {
            console.log('🧩 Game not found in marketplace collection');
          }
        }

        // Try user's purchased games collection
        if (!gameDoc && user?.uid) {
          try {
            const userGameRef = doc(db, 'users', user.uid, 'games', gameId);
            const snap = await getDoc(userGameRef);
            if (snap.exists()) {
              data = snap.data();
              gameDoc = { id: snap.id, ...data };
              console.log('🧩 Found game in user purchased games collection');
            }
          } catch (error) {
            console.log('🧩 Game not found in user purchased games collection');
          }
        }
        
        if (!cancelled) {
          if (gameDoc) {
            setGameData(gameDoc);
            console.log('🧩 GAME DOC LOADED', { id: gameDoc.id, hasTitle: !!gameDoc.title });
          } else {
            console.warn('❓ Game doc missing from all collections');
            setGameData(null);
          }
        }
      } catch (e) {
        console.error('🔥 Game load failed', e);
        if (!cancelled) setGameData(null);
      } finally {
        if (!cancelled) setGameLoaded(true);
      }
    };

    loadGameDoc();

    return () => { cancelled = true; };
  }, [gameId, db, user?.uid]);

  // Contract-compliant session loading
  useEffect(() => {
    const loadSessionByContract = async () => {
      if (!user?.uid || !gameId) {
        console.log('⏳ Waiting for auth ready and gameId...');
        return;
      }

      // One-shot guard to prevent multiple loads
      if (loadedOnceRef.current) {
        console.log('⏭️ loadSessionByContract skipped — already loaded once');
        return;
      }

      try {
        // 1) Prefer direct session by sessionId (when ?session= is present)
        if (sessionId) {
          console.log('📋 Loading session directly:', sessionId);
          const sessionPathFromQS = `sessions/${sessionId}`;
          const ref = doc(db, sessionPathFromQS);
          const snap = await getDoc(ref);
          if (snap.exists()) {
            const data = snap.data();
            console.log('✅ Session loaded directly:', data);
            // Hard-proof of active session → do NOT redirect
            if (data.ownerUid === user.uid && data.status === 'active') {
              console.log('🎯 Session verified: belongs to user and is active');
              setSessionReady(true); // Prevent any redirect loops
              sessionReadyRef.current = true; // Guard against fallback effects
              setLoading(false); // Clear loading state
              loadedOnceRef.current = true; // Mark as loaded once
              setSession(data); // Store session data for timer
              console.log('🔒 sessionReady set — bypassing pointer checks (loading=false)');
              // Set session state and proceed - DO NOT redirect
              return;
            } else {
              console.log('⚠️ Session exists but not owned by user or not active:', { 
                ownerUid: data.ownerUid, 
                userUid: user.uid, 
                status: data.status 
              });
            }
          } else {
            console.log('⚠️ Session not found, falling back to pointer');
          }
        }

        // 2) Fallback: Check for new unified session format (uid_gameId)
        if (!sessionReady) {
          console.log('📋 Loading session via unified format');
          const unifiedSessionId = `${user.uid}_${gameId}`;
          const unifiedRef = doc(db, 'sessions', unifiedSessionId);
          const unifiedSnap = await getDoc(unifiedRef);
          if (unifiedSnap.exists()) {
            const sessionData = unifiedSnap.data();
            console.log('✅ Unified session found:', sessionData);
            if (sessionData.ownerUid === user.uid && sessionData.status === 'active') {
              setSessionReady(true);
              sessionReadyRef.current = true;
              setLoading(false);
              loadedOnceRef.current = true;
              setSession(sessionData);
              console.log('🔒 Unified session ready');
              return;
            }
          } else {
            console.log('⚠️ Unified session not found, trying legacy pointer path');
          }
        }

        // 3) Legacy fallback: pointer path (only if needed and session not ready)
        if (!sessionReady) {
          console.log('📋 Loading session via legacy pointer path');
          const pointerRef = doc(db, 'users', user.uid, 'activeSessions', gameId);
          const pointerSnap = await getDoc(pointerRef);
          if (pointerSnap.exists()) {
            const pointerData = pointerSnap.data();
            console.log('✅ Pointer exists:', pointerData);
            const { sessionId: ptrSessionId, sessionPath } = pointerData || {};
            const ref = doc(db, sessionPath || `sessions/${ptrSessionId}`);
            const snap = await getDoc(ref);
            if (snap.exists()) {
              const sessionData = snap.data();
              console.log('✅ Session loaded via pointer:', sessionData);
              setSessionReady(true); // Prevent any redirect loops
              sessionReadyRef.current = true; // Guard against fallback effects
              setLoading(false); // Clear loading state
              loadedOnceRef.current = true; // Mark as loaded once
              setSession(sessionData); // Store session data for timer
              console.log('🔒 sessionReady set — bypassing pointer checks (loading=false)');
              // Set session state and proceed - DO NOT redirect
              return;
            }
          } else {
            console.log('⚠️ Legacy pointer not found');
          }
        }

        // 4) Only redirect if all session lookup methods fail and session not ready
        if (!sessionReady && !force) {
          console.log('❌ No session found via direct path or pointer, redirecting to My Games');
          // Diagnostic logs
          console.log('[DIAG] sessionId from URL:', sessionId);
          console.log('[DIAG] tried session path:', sessionId ? `sessions/${sessionId}` : 'none');
          console.log('[DIAG] pointer path:', `users/${user.uid}/activeSessions/${gameId}`);
          console.log('[DIAG] force mode:', force);
          console.log('[DIAG] session ready:', sessionReady);
          navigate('/my-games?resumeRequired=1');
        } else if (sessionReady) {
          console.log('🎯 Session ready, staying on page');
        } else {
          console.log('🔒 Force mode: staying on page despite no session');
        }
      } catch (error) {
        console.error('Session loading error:', error);
        // Don't treat permission-denied as "no session"
        if (error?.code === 'permission-denied') {
          console.error('🚫 Rules blocked session read. Check /sessions rules and ownerUid field.');
          setError('Permission denied: Unable to access session. Please try again.');
          return;
        }
        if (!force) {
          navigate('/my-games?error=sessionLoad');
        }
      }
    };

    loadSessionByContract();
  }, [sessionId, user?.uid, gameId, force, navigate, sessionReady]);

  // Check for launch session and show 12-hour warning modal
  const location = useLocation();
  useEffect(() => {
    const checkLaunchSession = async () => {
      const navState = location?.state;
      const cached = localStorage.getItem(`launchSession_${gameId}`);
      
      let sessionInfo = navState?.session || (cached ? JSON.parse(cached) : null);
      
      // If no session info, try to get it from the server
      if (!sessionInfo) {
        try {
          const { getFunctions, httpsCallable } = await import('firebase/functions');
          const functions = getFunctions();
          const getLaunchStatus = httpsCallable(functions, 'getLaunchStatus');
          const result = await getLaunchStatus({ gameId });
          if (result?.data?.ok) {
            sessionInfo = result.data.session;
            // Update session state with the current session data
            setSession(result.data.session);
          }
        } catch (error) {
          console.log('Could not fetch launch status:', error);
        }
      }
      
      if (sessionInfo && sessionInfo.isNewSession) {
        const ackKey = `launchWarnAck_${sessionInfo.sessionId}`;
        if (!localStorage.getItem(ackKey)) {
          setLaunchSession(sessionInfo);
          setShowLaunchWarning(true);
        }
      }
    };
    
    checkLaunchSession();
  }, [gameId]);

  // Helper functions for progress and upcoming categories
  const getProgressInfo = (stage) => {
    if (stage === 1) return { quarter: 'Pre-Game', question: null, totalQuestions: null };
    if (stage === 2) return { quarter: 'Quarter 1', question: 'Categories', totalQuestions: 5 };
    if (stage >= 3 && stage <= 7) return { quarter: 'Quarter 1', question: stage - 2, totalQuestions: 5 };
    if (stage === 8) return { quarter: 'Quarter 2', question: 'Categories', totalQuestions: 5 };
    if (stage >= 9 && stage <= 13) return { quarter: 'Quarter 2', question: stage - 8, totalQuestions: 5 };
    if (stage >= 14 && stage <= 18) return { quarter: 'Halftime', question: stage === 16 ? 1 : null, totalQuestions: 1 };
    if (stage === 19) return { quarter: 'Quarter 3', question: 'Categories', totalQuestions: 5 };
    if (stage >= 20 && stage <= 24) return { quarter: 'Quarter 3', question: stage - 19, totalQuestions: 5 };
    if (stage === 25) return { quarter: 'Quarter 4', question: 'Categories', totalQuestions: 5 };
    if (stage >= 26 && stage <= 30) return { quarter: 'Quarter 4', question: stage - 25, totalQuestions: 5 };
    if (stage >= 31 && stage <= 36) return { quarter: 'Final', question: stage === 35 ? 1 : null, totalQuestions: 1 };
    if (stage === 36) return { quarter: 'Tie Detection', question: null, totalQuestions: null };
    if (stage === 38) return { quarter: 'Tie Breaker', question: 1, totalQuestions: 1 };
    if (stage === 39) return { quarter: 'Final Results', question: null, totalQuestions: null };
    return { quarter: 'Game Over', question: null, totalQuestions: null };
  };

  const getUpcomingCategories = (stage, gameData) => {
    if (!gameData) return [];
    
    const { quarter1, quarter2, quarter3, quarter4, halftime, final } = gameData;
    
    // Get current quarter and question number
    const progress = getProgressInfo(stage);
    const currentQuarter = progress.quarter;
    const currentQuestion = progress.question;
    
    // If we're on categories stage, show all categories for that quarter
    if (currentQuestion === 'Categories') {
      if (currentQuarter === 'Quarter 1') return quarter1?.map(q => q.category) || [];
      if (currentQuarter === 'Quarter 2') return quarter2?.map(q => q.category) || [];
      if (currentQuarter === 'Quarter 3') return quarter3?.map(q => q.category) || [];
      if (currentQuarter === 'Quarter 4') return quarter4?.map(q => q.category) || [];
      if (currentQuarter === 'Halftime') return halftime?.category ? [halftime.category] : [];
      if (currentQuarter === 'Final') return final?.category ? [final.category] : [];
    }
    
    // If we're on a question, show remaining categories in current quarter
    if (typeof currentQuestion === 'number') {
      let quarter;
      if (currentQuarter === 'Quarter 1') quarter = quarter1;
      else if (currentQuarter === 'Quarter 2') quarter = quarter2;
      else if (currentQuarter === 'Quarter 3') quarter = quarter3;
      else if (currentQuarter === 'Quarter 4') quarter = quarter4;
      else if (currentQuarter === 'Halftime') return [];
      else if (currentQuarter === 'Final') return [];
      else return [];
      
      if (!quarter) return [];
      
      // Return remaining categories (current question and beyond)
      return quarter.slice(currentQuestion - 1).map(q => q.category);
    }
    
    return [];
  };

  // Enhanced game expiration check - checks game session instead of game creation time
  const checkGameExpiration = useCallback(async (gameData) => {
    if (!gameData || !user) return;
    
    try {
      // Check if there's an active game session for this game
      const gameSessionsRef = collection(db, 'gameSessions');
      const existingSessionQuery = query(
        gameSessionsRef, 
        where('userId', '==', user.uid),
        where('gameId', '==', gameId),
        where('isPurchasedGame', '==', true),
        where('status', '==', 'active')
      );
      const existingSessionSnapshot = await getDocs(existingSessionQuery);
      
      if (!existingSessionSnapshot.empty) {
        // Find the most recent active session (in case there are multiple)
        let mostRecentSession = existingSessionSnapshot.docs[0];
        let mostRecentTime = 0;
        
        existingSessionSnapshot.docs.forEach(doc => {
          const sessionData = doc.data();
          const launchTime = sessionData.launchTime;
          if (launchTime && typeof launchTime.toDate === 'function') {
            const time = launchTime.toDate().getTime();
            if (time > mostRecentTime) {
              mostRecentTime = time;
              mostRecentSession = doc;
            }
          }
        });
        
        const sessionData = mostRecentSession.data();
        
        // Handle both Firestore Timestamp and regular Date objects
        let expiresAt;
        if (sessionData.expiresAt && typeof sessionData.expiresAt.toDate === 'function') {
          expiresAt = sessionData.expiresAt.toDate();
        } else if (sessionData.expiresAt) {
          expiresAt = new Date(sessionData.expiresAt);
        } else {
          // No expiration set, treat as expired
          expiresAt = new Date(0);
        }
        
        const now = new Date();
        console.log('Game expiration check (UTC):', {
          expiresAt: expiresAt.toISOString(),
          now: now.toISOString(),
          isExpired: now >= expiresAt,
          sessionId: mostRecentSession.id
        });
        
        if (now >= expiresAt) {
          // Session expired - mark it as expired
          console.log('Game session expired, marking as inactive');
          await updateDoc(doc(db, 'gameSessions', mostRecentSession.id), {
            status: 'expired'
          });
          
          setError('This game session has expired. Please start a new game.');
          setGameData(null);
          return;
        }
      } else {
        // No active session found - this might be a user's own game
        // For user's own games, we don't check expiration based on creation time
        console.log('No active session found - likely user\'s own game');
      }
      
      // Check if this is the first launch (for tracking purposes)
      // Only update if this is a user's own game, not a purchased game
      if (!gameData.firstLaunch && gameData.creatorId === user?.uid) {
        const updateFirstLaunch = async () => {
          try {
            // Only update if the game exists in the games collection (user's own games)
            const gameDocRef = doc(db, 'games', gameId);
            const gameDoc = await getDoc(gameDocRef);
            
            if (gameDoc.exists()) {
              await updateDoc(gameDocRef, {
                firstLaunch: serverTimestamp()
              });
              console.log('Updated firstLaunch timestamp for game:', gameId);
            } else {
              console.log('Game not found in games collection, skipping firstLaunch update');
            }
          } catch (error) {
            console.error('Error updating first launch:', error);
            // Don't throw the error - just log it and continue
          }
        };
        updateFirstLaunch();
      }
    } catch (error) {
      console.error('Error checking game expiration:', error);
    }
  }, [gameId, user]);

  // 🎯 FAN-OUT: Download game once and store locally
  useEffect(() => {
    // If we have a verified session from URL, skip fallback entirely
    if (sessionId && sessionReadyRef.current) {
      console.log('🛑 Skipping fallback — sessionReady true');
      return;
    }

    // Hard guard: if session is already loaded once, skip entirely
    if (loadedOnceRef.current) {
      console.log('🛑 Skipping fallback — session already loaded once');
      return;
    }

    const loadGameLocally = async () => {
      // Hard guard: if session is already verified, abort
      if (sessionReadyRef.current || loadedOnceRef.current) {
        console.log('🛑 Fallback aborted — session already verified');
        return;
      }

      // If we have a sessionId from URL, don't run fallback logic
      if (params.get('session')) {
        console.log('🛑 Fallback aborted — sessionId from URL present');
        return;
      }

      if (!gameId) {
        setError('No game ID provided');
        setLoading(false);
        return;
      }

      if (!user?.uid) {
        console.log('User not loaded yet, waiting...');
        return;
      }

      try {
        // Always clear cache for this game on load
        clearGameCache(gameId);

        // Try to load game from multiple possible locations
        console.log('🌐 FAN-OUT: Downloading from FIRESTORE (one-time)');
        let gameDoc = null;
        let data = null;

        // Try regular games collection first (personal games)
        try {
          gameDoc = await getDoc(doc(db, 'games', gameId));
          if (gameDoc.exists()) {
            data = gameDoc.data();
            console.log('Found game in regular games collection');
          }
        } catch (error) {
          console.log('Game not found in regular games collection');
        }

        // Try marketplace games collection
        if (!gameDoc || !gameDoc.exists()) {
          try {
            gameDoc = await getDoc(doc(db, 'games', 'content', 'triviaGames', gameId));
            if (gameDoc.exists()) {
              data = gameDoc.data();
              console.log('Found game in marketplace collection');
            }
          } catch (error) {
            console.log('Game not found in marketplace collection');
          }
        }

        // Try user's purchased games collection
        if (!gameDoc || !gameDoc.exists()) {
          try {
            gameDoc = await getDoc(doc(db, 'users', user?.uid, 'games', gameId));
            if (gameDoc.exists()) {
              data = gameDoc.data();
              console.log('Found game in user purchased games collection');
            }
          } catch (error) {
            console.log('Game not found in user purchased games collection');
          }
        }

        if (!gameDoc || !gameDoc.exists()) {
          setError('Game not found');
          setLoading(false);
          return;
        }

        // Determine sessionId: prefer URL parameter, then navigation state, then user document
        let sessionId = params.get('session') || sessionIdFromNav;
        
        if (!sessionId && user?.uid) {
          // Fallback: try to read from user document if available
          try {
            const userDoc = await getDoc(doc(db, `users/${user.uid}`));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              const activeSession = userData?.activeSessions?.[gameId];
              if (activeSession && activeSession.expiresAt?.toMillis?.() > Date.now()) {
                sessionId = activeSession.sessionId;
                console.log('Found active session in user document:', sessionId);
              }
            }
          } catch (error) {
            console.warn('Could not read user document for session fallback:', error);
          }
        }
        
        if (!sessionId) {
          // Check if session is already ready to prevent redirect
          if (sessionReadyRef.current) {
            console.log('🛑 Redirect suppressed — sessionReady true');
            return;
          }
          console.warn('No active session found. Redirecting user to My Games to resume or start.');
          navigate('/my-games?resumeRequired=1', { replace: true });
          return;
        }
        
        // For direct launches (no venue scheduling), use session data
        if (!data.venueId) {
          data = {
            ...data,
            venueId: 'direct-launch',
            venueName: 'Direct Launch',
            venueCity: 'Test',
            venueState: 'Test',
            venueCountry: 'Test',
            venueChain: 'Test',
            scheduledDate: new Date().toISOString(),
            scheduledTime: new Date().toISOString(),
            gameType: 'direct-launch',
            sessionId: sessionId
          };
          console.log('Using session:', sessionId);
        }
        
        // Cache game data locally for offline use
        console.log('💾 FAN-OUT: Caching game data locally');
        cacheGameData(gameId, data);
        
        // Initialize game state
        const initialState = {
          teams: [],
          currentStage: 1,
          revealAnswer: false,
          gameComplete: false,
          currentSlideIndex: 0
        };
        cacheGameState(gameId, initialState);
        
        setGameData(data);
        setTeams([]);
        setCurrentStage(1);
        setRevealAnswer(false);
        setHostPeekAnswer(false);
        setCurrentSlideIndex(0);
        
        // 🚫 DISABLED: Initialize Firestore sync - everything runs in RAM
        console.log('[INIT] Game loaded into RAM - no database writes');
        
        // Load league teams for search functionality
        loadLeagueTeams();
        
        setLoading(false);
        
        // Check for game expiration
        checkGameExpiration(data);
      } catch (err) {
        console.error('Error loading game:', err);
        setError('Failed to load game');
        setLoading(false);
      }
    };

    loadGameLocally();
  }, [gameId, user, checkGameExpiration, sessionId, sessionReadyRef, loadedOnceRef]);

  // Fetch slides for the current venue/game
  useEffect(() => {
    const fetchSlides = async () => {
      if (!gameData) return;

      try {
        // Get venue info from game data
        const venueId = gameData.venueId;
        const venueCity = gameData.venueCity;
        const venueState = gameData.venueState;
        const venueCountry = gameData.venueCountry;
        const venueChain = gameData.venueChain;

        // Build query for slides that apply to this venue
        const slidesQuery = query(
          collection(db, 'slides'),
          where('active', '==', true)
        );
        
        const snapshot = await getDocs(slidesQuery);
        const allSlides = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Filter slides based on targeting
        const applicableSlides = allSlides.filter(slide => {
          if (slide.targetType === 'global') return true;
          if (slide.targetType === 'venue' && slide.targetValue === venueId) return true;
          if (slide.targetType === 'city' && slide.targetValue === venueCity) return true;
          if (slide.targetType === 'state' && slide.targetValue === venueState) return true;
          if (slide.targetType === 'country' && slide.targetValue === venueCountry) return true;
          if (slide.targetType === 'chain' && slide.targetValue === venueChain) return true;
          return false;
        });

        // Sort by stage, then by order within each stage
        applicableSlides.sort((a, b) => {
          // First sort by stage priority: pregame -> halftime -> tiebreaker -> final
          const stageOrder = { pregame: 1, halftime: 2, tiebreaker: 3, final: 4 };
          const stageA = stageOrder[a.stage] || 5;
          const stageB = stageOrder[b.stage] || 5;
          
          if (stageA !== stageB) {
            return stageA - stageB;
          }
          
          // Then sort by order within each stage
          const orderA = a.order || 1;
          const orderB = b.order || 1;
          return orderA - orderB;
        });
        
        setSlides(applicableSlides);
        console.log(`📢 Loaded ${applicableSlides.length} applicable slides`);
      } catch (error) {
        console.error('Error fetching slides:', error);
      }
    };

    fetchSlides();
  }, [gameData]);

  // Auto-advance slides
  // useEffect(() => {
  //   if (isPreGameSlideStage(currentStage) && slides.length > 0) {
  //     const currentSlide = slides[currentSlideIndex];
  //     if (currentSlide && currentSlide.duration) {
  //       const timer = setTimeout(() => {
  //         if (currentSlideIndex < slides.length - 1) {
  //           setCurrentSlideIndex(prev => prev + 1);
  //         } else {
  //           // Move to next stage when all slides are shown
  //           setCurrentStage(prev => prev + 1);
  //           setCurrentSlideIndex(0);
  //         }
  //       }, currentSlide.duration * 1000);
  //
  //       return () => clearTimeout(timer);
  //     }
  //   }
  // }, [currentStage, currentSlideIndex, slides]);

  // const isPreGameSlideStage = (stage) => {
  //   return [1, 2, 3, 4].includes(stage); // Only pre-game slides auto-advance
  // }; // Removed unused function

  useEffect(() => {
    if (gameData) {
      checkGameExpiration(gameData);
    }
  }, [gameData, checkGameExpiration]);

  // 🎯 SYNC: Update audience display via localStorage (handled by protection system)
  useEffect(() => {
    if (gameData && isGameActive) {
      // Sync stage with audience display (localStorage)
      updateHostStage(gameId, currentStage, revealAnswer, scoreAnnouncementIndex, teams, currentSlideIndex);
      
      console.log('🎯 SYNC: Updated audience display', {
        currentStage,
        revealAnswer,
        scoreAnnouncementIndex,
        teamsCount: teams.length
      });
    }
  }, [teams, currentStage, revealAnswer, scoreAnnouncementIndex, currentSlideIndex, gameData, gameId, isGameActive]);

  // 🚫 DISABLED: Firestore sync - everything runs in RAM now
  // useEffect(() => {
  //   if (gameId && typeof gameId === 'string' && gameId.trim() !== '') {
  //     console.log('[SYNC] setHostStageInFirestore:', { 
  //       gameId, 
  //       currentStage, 
  //       revealAnswer, 
  //       scoreAnnouncementIndex, 
  //       teamsCount: teams.length,
  //       teams: teams
  //     });
  //     
  //     // Wrap in try-catch to see exactly where the error occurs
  //     try {
  //       setHostStageInFirestore(gameId, currentStage, revealAnswer, scoreAnnouncementIndex, teams);
  //     } catch (error) {
  //       console.error('❌ Error in setHostStageInFirestore:', error);
  //       console.error('❌ Error details:', {
  //         gameId,
  //         currentStage,
  //         revealAnswer,
  //         scoreAnnouncementIndex,
  //         teams
  //       });
  //     }
  //   } else {
  //     console.log('[SYNC] Skipping Firestore sync - invalid gameId:', gameId);
  //   }
  // }, [gameId, currentStage, revealAnswer, scoreAnnouncementIndex, teams]);

  // 🚫 DISABLED: Initial Firestore sync
  // useEffect(() => {
  //   if (!loading && !error && gameId && typeof gameId === 'string' && gameId.trim() !== '' && currentStage === 1) {
  //     console.log('[SYNC] Setting Firestore stage to 1 (initial load)');
  //     try {
  //       setHostStageInFirestore(gameId, 1, false, -1, teams);
  //     } catch (error) {
  //       console.error('❌ Error in initial load Firestore sync:', error);
  //     }
  //   }
  // }, [loading, error, gameId, currentStage, teams]);

  useEffect(() => {
    console.log('[HOST] currentStage changed:', currentStage);
  }, [currentStage]);

  // Monitor and restore fullscreen when manual entry modal opens
  useEffect(() => {
    if (!showManualEntryModal) return;

    // Capture fullscreen state before modal opens
    if (!wasFullscreenBeforeModalRef.current) {
      wasFullscreenBeforeModalRef.current = !!document.fullscreenElement || protection.isFullscreen;
    }

    const checkAndRestoreFullscreen = () => {
      const isCurrentlyFullscreen = !!document.fullscreenElement;
      
      // If we were in fullscreen but browser exited it when modal opened
      if (wasFullscreenBeforeModalRef.current && !isCurrentlyFullscreen) {
        console.log('🖥️ Fullscreen was lost when modal opened, restoring...');
        // Attempt to re-enter fullscreen
        document.documentElement.requestFullscreen().catch(err => {
          console.warn('Could not restore fullscreen:', err);
        });
      }
    };

    // Check immediately when modal opens
    checkAndRestoreFullscreen();

    // Monitor fullscreen changes
    const handleFullscreenChange = () => {
      checkAndRestoreFullscreen();
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    
    // Also use a polling approach as backup (browser might exit fullscreen silently)
    const pollInterval = setInterval(() => {
      checkAndRestoreFullscreen();
    }, 300); // Check every 300ms while modal is open

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      clearInterval(pollInterval);
      // Reset ref when modal closes
      wasFullscreenBeforeModalRef.current = false;
    };
  }, [showManualEntryModal, protection.isFullscreen]);

  // Load league teams for search (called once at game start)
  const loadLeagueTeams = async () => {
    try {
      console.log('🔍 Loading league teams for host...');
      
      if (!user?.uid) {
        console.warn('No user ID, cannot load personalized teams');
        setLeagueTeams([]);
        setRecentTeams([]);
        return;
      }

      // Load all teams from the teams collection (for general search)
      const teamsQuery = query(collection(db, 'teams'));
      const teamsSnapshot = await getDocs(teamsQuery);
      
      const realLeagueTeams = teamsSnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name,
        leagueId: doc.data().leagueId,
        homeVenue: doc.data().homeVenue || 'Unknown Venue',
        gpa: doc.data().gpa || 0,
        description: doc.data().description,
        captain: doc.data().captain,
        members: doc.data().members || []
      }));
      
      console.log(`✅ Loaded ${realLeagueTeams.length} real teams from database`);
      setLeagueTeams(realLeagueTeams);
      
      // Load recent and frequent teams from this host's game results
      await loadHostTeams(user.uid);
      
      // Also load recent teams from localStorage as fallback
      const localStorageRecent = JSON.parse(localStorage.getItem('recentTeams') || '[]');
      if (localStorageRecent.length > 0 && recentTeams.length === 0) {
        setRecentTeams(localStorageRecent);
      }
    } catch (error) {
      console.error('Error loading league teams:', error);
      // Fallback to empty array if there's an error
      setLeagueTeams([]);
    }
  };

  // Load teams that have played with this host recently and frequently
  const loadHostTeams = async (hostUid) => {
    try {
      console.log(`🔍 Loading teams for host ${hostUid}...`);
      
      // Query gameResults for this host, ordered by completion date (most recent first)
      const resultsQuery = query(
        collection(db, 'gameResults'),
        where('hostId', '==', hostUid),
        orderBy('completedAt', 'desc'),
        limit(50) // Get last 50 games
      );
      
      const resultsSnapshot = await getDocs(resultsQuery);
      
      // Track teams by leagueId with frequency and last played date
      const teamMap = new Map(); // Key: leagueId, Value: { team data, frequency, lastPlayed }
      
      resultsSnapshot.docs.forEach(doc => {
        const resultData = doc.data();
        
        // Handle Firestore Timestamp conversion
        let completedAt = null;
        if (resultData.completedAt) {
          if (resultData.completedAt.toDate) {
            completedAt = resultData.completedAt.toDate();
          } else if (resultData.completedAt instanceof Date) {
            completedAt = resultData.completedAt;
          } else {
            completedAt = new Date(resultData.completedAt);
          }
        } else {
          // Fallback to current date if no completion date
          completedAt = new Date();
        }
        
        // Extract teams from result
        const resultTeams = resultData.teams || [];
        
        resultTeams.forEach(team => {
          const leagueId = team.leagueId || team.id;
          if (!leagueId) return; // Skip teams without leagueId
          
          if (!teamMap.has(leagueId)) {
            // First time seeing this team - get full team data from teams collection or use result data
            teamMap.set(leagueId, {
              id: team.id,
              leagueId: leagueId,
              name: team.name || 'Unknown Team',
              homeVenue: team.homeVenue || 'Unknown Venue',
              gpa: team.gpa || 0,
              frequency: 0,
              lastPlayed: null,
              playedDates: []
            });
          }
          
          const teamData = teamMap.get(leagueId);
          teamData.frequency += 1;
          if (!teamData.lastPlayed || completedAt > teamData.lastPlayed) {
            teamData.lastPlayed = completedAt;
          }
          teamData.playedDates.push(completedAt);
        });
      });
      
      // Convert map to array and sort by frequency (most frequent first), then by recency
      const hostTeams = Array.from(teamMap.values())
        .sort((a, b) => {
          // First sort by frequency (most frequent first)
          if (b.frequency !== a.frequency) {
            return b.frequency - a.frequency;
          }
          // Then sort by most recently played
          if (b.lastPlayed && a.lastPlayed) {
            return b.lastPlayed.getTime() - a.lastPlayed.getTime();
          }
          return 0;
        })
        .slice(0, 20); // Keep top 20 teams
      
      console.log(`✅ Found ${hostTeams.length} teams that have played with this host`);
      setRecentTeams(hostTeams);
      
    } catch (error) {
      console.error('Error loading host teams:', error);
      
      // If it's a missing index error, log it but continue with fallback
      if (error.code === 'failed-precondition' || error.message?.includes('index')) {
        console.warn('⚠️ Firestore index missing for gameResults query. Please create an index for gameResults with fields: hostId (ASC) and completedAt (DESC)');
      }
      
      // Fallback to localStorage recent teams
      const localStorageRecent = JSON.parse(localStorage.getItem('recentTeams') || '[]');
      if (localStorageRecent.length > 0) {
        setRecentTeams(localStorageRecent);
      }
    }
  };

  // Search league teams locally
  const searchLeagueTeams = (query) => {
    if (!query.trim()) {
      setFilteredLeagueTeams([]);
      return;
    }
    
    const searchTerm = query.toLowerCase();
    console.log('🔍 Searching for:', searchTerm);
    console.log('📋 Available teams:', leagueTeams);
    
    const filtered = leagueTeams.filter(team => {
      const nameMatch = team.name.toLowerCase().includes(searchTerm);
      const leagueIdMatch = team.leagueId.toLowerCase().includes(searchTerm);
      const venueMatch = team.homeVenue.toLowerCase().includes(searchTerm);
      
      console.log(`Team "${team.name}" (${team.leagueId}):`, {
        nameMatch,
        leagueIdMatch,
        venueMatch,
        name: team.name.toLowerCase(),
        leagueId: team.leagueId.toLowerCase(),
        venue: team.homeVenue.toLowerCase()
      });
      
      return nameMatch || leagueIdMatch || venueMatch;
    });
    
    console.log('✅ Search results:', filtered);
    setFilteredLeagueTeams(filtered);
  };

  // Add team from search results
  const addLeagueTeam = (leagueTeam) => {
    // Check if team is already in the game
    const isAlreadyInGame = teams.some(team => team.leagueId === leagueTeam.leagueId);
    if (isAlreadyInGame) {
      alert('This team is already in the game!');
      return;
    }

    const newTeam = {
      id: uuidv4(),
      name: leagueTeam.name,
      leagueId: leagueTeam.leagueId,
      homeVenue: leagueTeam.homeVenue,
      gpa: leagueTeam.gpa,
      score: 0,
      usedPoints: {},
      correctAnswers: {},
      wrongAnswers: {},
      halftimeScore: 0,
      finalScore: 0,
    };
    
    setTeams([...teams, newTeam]);
    
    // Add to recent teams
    const updatedRecent = [leagueTeam, ...recentTeams.filter(t => t.id !== leagueTeam.id)].slice(0, 10);
    setRecentTeams(updatedRecent);
    localStorage.setItem('recentTeams', JSON.stringify(updatedRecent));
    
    setShowTeamSearchModal(false);
    setTeamSearchQuery('');
    setFilteredLeagueTeams([]);
  };

  // Add manual team (using custom modal to preserve fullscreen)
  const addManualTeam = (e) => {
    // Prevent default to avoid any browser behavior
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Capture fullscreen state before opening modal
    const isCurrentlyFullscreen = !!document.fullscreenElement || protection.isFullscreen;
    wasFullscreenBeforeModalRef.current = isCurrentlyFullscreen;
    
    // Open custom modal instead of using prompt() which exits fullscreen
    setShowManualEntryModal(true);
    setManualTeamName('');
    
    // Immediately try to maintain fullscreen - browser might exit it on modal open
    // Use requestAnimationFrame to ensure it happens after browser's exit
    if (isCurrentlyFullscreen) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen().catch(() => {
              // Browser blocked it - that's okay, we'll monitor and restore
            });
          }
        });
      });
    }
  };

  // Handle manual team name submission
  const handleManualTeamSubmit = () => {
    const name = manualTeamName.trim();
    if (!name) return;
    
    const newTeam = {
      id: uuidv4(),
      name,
      score: 0,
      usedPoints: {},
      correctAnswers: {},
      wrongAnswers: {},
      halftimeScore: 0,
      finalScore: 0,
      gpa: 0, // Initialize GPA to 0
    };
    setTeams([...teams, newTeam]);
    setShowManualEntryModal(false);
    setManualTeamName('');
    // Also close the team search modal if it's open
    setShowTeamSearchModal(false);
  };

  // Enhanced add team function
  const addTeam = () => {
    setShowTeamSearchModal(true);
    // Load league teams if not already loaded
    if (leagueTeams.length === 0) {
      loadLeagueTeams();
    }
  };

  const deleteTeam = (id) => {
    if (window.confirm('Delete this team?')) {
      setTeams(teams.filter((t) => t.id !== id));
    }
  };

  // Helper function to determine current quarter from stage
  const getCurrentQuarter = (stage) => {
    if (stage >= 3 && stage <= 7) return 1;   // Quarter 1 questions
    if (stage >= 9 && stage <= 13) return 2;  // Quarter 2 questions
    if (stage >= 20 && stage <= 24) return 3; // Quarter 3 questions
    if (stage >= 26 && stage <= 30) return 4; // Quarter 4 questions
    if (stage === 16) return 'halftime';      // Halftime question
    if (stage === 35) return 'final';         // Final question
    if (stage === 38) return 'tieBreaker';    // Tie breaker
    return 0; // Pre-game or other stages
  };

  const currentQuarter = getCurrentQuarter(currentStage);

  // Helper function to determine if scoring should be enabled for current stage
  const isScoringEnabled = (stage) => {
    // Enable scoring only for actual question stages
    // Quarter 1 questions: stages 3-7
    if (stage >= 3 && stage <= 7) return true;
    // Quarter 2 questions: stages 9-13  
    if (stage >= 9 && stage <= 13) return true;
    // Quarter 3 questions: stages 20-24
    if (stage >= 20 && stage <= 24) return true;
    // Quarter 4 questions: stages 26-30
    if (stage >= 26 && stage <= 30) return true;
    // Tiebreaker: stage 38 - DISABLED (only winner selection, no regular scoring)
    // if (stage === 38) return true; // Removed tiebreaker scoring
    
    // Disable scoring for all other stages (categories, ads, slides, tiebreaker, etc.)
    return false;
  };

  // Helper function to get question index
  const getQuestionIndex = (stage, quarter) => {
    if (typeof quarter === 'string') return 0;
    // Calculate question index based on current stage (0-based to match array indexing)
    if (quarter === 1) return stage - 3; // Stage 3 = Q1, Q1 = index 0, Stage 7 = Q5, Q5 = index 4
    else if (quarter === 2) return stage - 9; // Stage 9 = Q2, Q1 = index 0
    else if (quarter === 3) return stage - 20; // Stage 20 = Q3, Q1 = index 0
    else if (quarter === 4) return stage - 26; // Stage 26 = Q4, Q1 = index 0
    return 0;
  };

  const handleScore = (teamId, points) => {
    setTeams((prev) =>
      prev.map((team) => {
        if (team.id === teamId) {
          // Get current question index
          const questionIndex = getQuestionIndex(currentStage, currentQuarter);
          
          const used = team.usedPoints?.[currentQuarter] || [];
          // Check if this point value is already used (for backward compatibility)
          if (used.some(entry => typeof entry === 'number' ? entry === points : entry.points === points)) return team;
          
          let updatedTeam;
          
          // For special sections (halftime, final, tieBreaker), only allow one point value
          if (typeof currentQuarter === 'string') {
            updatedTeam = {
              ...team,
              score: team.score + points,
              usedPoints: {
                ...team.usedPoints,
                [currentQuarter]: [{ questionIndex, points }] // Only one point value allowed
              },
              correctAnswers: {
                ...team.correctAnswers,
                [currentQuarter]: [...(team.correctAnswers?.[currentQuarter] || []), { questionIndex, points }]
              }
            };
            
            // 🚨 DEBUG: Log scoring for special quarters
            console.log('🎯 SCORING (Special):', {
              teamName: team.name,
              quarter: currentQuarter,
              points,
              questionIndex,
              updatedUsedPoints: updatedTeam.usedPoints,
              updatedCorrectAnswers: updatedTeam.correctAnswers
            });
          } else {
            updatedTeam = {
              ...team,
              score: team.score + points,
              usedPoints: {
                ...team.usedPoints,
                [currentQuarter]: [...used, { questionIndex, points }]
              },
              correctAnswers: {
                ...team.correctAnswers,
                [currentQuarter]: [...(team.correctAnswers?.[currentQuarter] || []), { questionIndex, points }]
              }
            };
            
            // 🚨 DEBUG: Log scoring for regular quarters
            console.log('🎯 SCORING (Regular):', {
              teamName: team.name,
              quarter: currentQuarter,
              points,
              questionIndex,
              updatedUsedPoints: updatedTeam.usedPoints,
              updatedCorrectAnswers: updatedTeam.correctAnswers
            });
          }
          
          // Calculate GPA for this team
          updatedTeam.gpa = calculateTeamGPA(updatedTeam);
          
          return updatedTeam;
        }
        return team;
      })
    );
    
    // Mark this team as having answered the current question
    setTeamsAnsweredCurrentQuestion(prev => new Set([...prev, teamId]));
  };

  const handleWrongAnswer = (teamId, points) => {
    setTeams((prev) =>
      prev.map((team) => {
        if (team.id === teamId) {
          // Get current question index
          const questionIndex = getQuestionIndex(currentStage, currentQuarter);
          
          const used = team.usedPoints?.[currentQuarter] || [];
          if (used.some(entry => typeof entry === 'number' ? entry === points : entry.points === points)) return team;
          
          // For special sections (halftime, final, tieBreaker), only allow one point value
          if (typeof currentQuarter === 'string') {
            return {
              ...team,
              usedPoints: {
                ...team.usedPoints,
                [currentQuarter]: [{ questionIndex, points }] // Only one point value allowed
              },
              wrongAnswers: {
                ...team.wrongAnswers,
                [currentQuarter]: [...(team.wrongAnswers?.[currentQuarter] || []), { questionIndex, points }]
              }
            };
          }
          
          return {
            ...team,
            usedPoints: {
              ...team.usedPoints,
              [currentQuarter]: [...used, { questionIndex, points }]
            },
            wrongAnswers: {
              ...team.wrongAnswers,
              [currentQuarter]: [...(team.wrongAnswers?.[currentQuarter] || []), { questionIndex, points }]
            }
          };
        }
        return team;
      })
    );
    
    // Mark this team as having answered the current question
    setTeamsAnsweredCurrentQuestion(prev => new Set([...prev, teamId]));
  };

  const handleUndo = (teamId, points) => {
    setTeams((prev) =>
      prev.map((team) => {
        if (team.id === teamId) {
          // Get current question index
          const questionIndex = getQuestionIndex(currentStage, currentQuarter);
          
          // Check if there are actually points to undo
          const hasCorrectAnswer = (team.correctAnswers?.[currentQuarter] || []).some(entry => 
            typeof entry === 'object' && entry.questionIndex === questionIndex && entry.points === points
          );
          const hasWrongAnswer = (team.wrongAnswers?.[currentQuarter] || []).some(entry => 
            typeof entry === 'object' && entry.questionIndex === questionIndex && entry.points === points
          );
          
          // Only undo if there are points to undo
          if (!hasCorrectAnswer && !hasWrongAnswer) {
            return team; // No points to undo, return team unchanged
          }
          
          // Remove entries for this question index and point value
          const used = (team.usedPoints?.[currentQuarter] || []).filter(entry => 
            !(typeof entry === 'object' && entry.questionIndex === questionIndex && entry.points === points)
          );
          const correctAnswers = (team.correctAnswers?.[currentQuarter] || []).filter(entry => 
            !(typeof entry === 'object' && entry.questionIndex === questionIndex && entry.points === points)
          );
          const wrongAnswers = (team.wrongAnswers?.[currentQuarter] || []).filter(entry => 
            !(typeof entry === 'object' && entry.questionIndex === questionIndex && entry.points === points)
          );
          
          // Only subtract points if there was originally a correct answer (points were added)
          const newScore = hasCorrectAnswer ? team.score - points : team.score;
          
          const updatedTeam = {
            ...team,
            score: newScore,
            usedPoints: {
              ...team.usedPoints,
              [currentQuarter]: used
            },
            correctAnswers: {
              ...team.correctAnswers,
              [currentQuarter]: correctAnswers
            },
            wrongAnswers: {
              ...team.wrongAnswers,
              [currentQuarter]: wrongAnswers
            }
          };
          
          // Calculate GPA for this team
          updatedTeam.gpa = calculateTeamGPA(updatedTeam);
          
          return updatedTeam;
        }
        return team;
      })
    );
    
    // Remove this team from answered list when they undo
    setTeamsAnsweredCurrentQuestion(prev => {
      const newSet = new Set(prev);
      newSet.delete(teamId);
      return newSet;
    });
  };



  // Enhanced manual scoring for halftime and final
  const handleHalftimeScore = (teamId, value) => {
    const num = parseInt(value, 10);
    if (isNaN(num)) return;
    setTeams((prev) =>
      prev.map((team) => {
        if (team.id === teamId) {
          return {
            ...team,
            halftimeScore: num
          };
        }
        return team;
      })
    );
  };

  const handleFinalScore = (teamId, value) => {
    const num = parseInt(value, 10);
    if (isNaN(num)) return;
    setTeams((prev) =>
      prev.map((team) => {
        if (team.id === teamId) {
          return {
            ...team,
            finalScore: num
          };
        }
        return team;
      })
    );
  };

  const submitHalftimeScores = () => {
    setHalftimeUndoClicked(false); // Reset undo flag
    
    setTeams((prev) =>
      prev.map((team) => {
        if (team.halftimeScore !== undefined) {
          const updatedTeam = {
            ...team,
            score: team.score + team.halftimeScore,
            halftimeScore: undefined, // Clear the temporary score
            correctAnswers: {
              ...team.correctAnswers,
              halftime: [{ questionIndex: 0, points: team.halftimeScore }]
            }
          };
          
          // Calculate GPA for this team
          updatedTeam.gpa = calculateTeamGPA(updatedTeam);
          
          return updatedTeam;
        }
        return team;
      })
    );
    
    // Mark all teams with halftime scores as answered
    const teamsWithHalftimeScores = teams.filter(team => team.halftimeScore !== undefined);
    setTeamsAnsweredCurrentQuestion(prev => new Set([...prev, ...teamsWithHalftimeScores.map(team => team.id)]));
  };

  const submitIndividualHalftimeScore = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    if (!team || team.halftimeScore === undefined) return;
    
    setTeams((prev) =>
      prev.map((t) => {
        if (t.id === teamId) {
          const updatedTeam = {
            ...t,
            score: t.score + t.halftimeScore,
            halftimeScore: undefined, // Clear the temporary score
            correctAnswers: {
              ...t.correctAnswers,
              halftime: [{ questionIndex: 0, points: t.halftimeScore }]
            }
          };
          
          // Calculate GPA for this team
          updatedTeam.gpa = calculateTeamGPA(updatedTeam);
          
          return updatedTeam;
        }
        return t;
      })
    );
    
    // Mark this specific team as answered
    setTeamsAnsweredCurrentQuestion(prev => new Set([...prev, teamId]));
  };

  const submitFinalScores = () => {
    setFinalUndoClicked(false); // Reset undo flag
    
    setTeams((prev) =>
      prev.map((team) => {
        if (team.finalScore !== undefined) {
          const updatedTeam = {
            ...team,
            score: team.score + team.finalScore,
            finalScore: undefined, // Clear the temporary score
            correctAnswers: {
              ...team.correctAnswers,
              final: [{ questionIndex: 0, points: team.finalScore }]
            }
          };
          
          // Calculate GPA for this team
          updatedTeam.gpa = calculateTeamGPA(updatedTeam);
          
          return updatedTeam;
        }
        return team;
      })
    );
    
    // Mark all teams with final scores as answered
    const teamsWithFinalScores = teams.filter(team => team.finalScore !== undefined);
    setTeamsAnsweredCurrentQuestion(prev => new Set([...prev, ...teamsWithFinalScores.map(team => team.id)]));
  };

  const submitIndividualFinalScore = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    if (!team || team.finalScore === undefined) return;
    
    setTeams((prev) =>
      prev.map((t) => {
        if (t.id === teamId) {
          const updatedTeam = {
            ...t,
            score: t.score + t.finalScore,
            finalScore: undefined, // Clear the temporary score
            correctAnswers: {
              ...t.correctAnswers,
              final: [{ questionIndex: 0, points: t.finalScore }]
            }
          };
          
          // Calculate GPA for this team
          updatedTeam.gpa = calculateTeamGPA(updatedTeam);
          
          return updatedTeam;
        }
        return t;
      })
    );
    
    // Mark this specific team as answered
    setTeamsAnsweredCurrentQuestion(prev => new Set([...prev, teamId]));
  };

  const [halftimeUndoClicked, setHalftimeUndoClicked] = useState(false);
  
  const undoHalftimeScores = () => {
    if (halftimeUndoClicked) return; // Prevent multiple clicks
    
    setHalftimeUndoClicked(true);
    
    setTeams((prev) =>
      prev.map((team) => {
        // Only undo if there are halftime scores to undo
        if (team.correctAnswers?.['halftime']?.length > 0) {
          const pointsToRemove = team.correctAnswers['halftime'][0].points;
          return {
            ...team,
            score: team.score - pointsToRemove,
            correctAnswers: {
              ...team.correctAnswers,
              halftime: []
            }
          };
        }
        return team;
      })
    );
    
    // Remove teams from answered list when undoing
    setTeamsAnsweredCurrentQuestion(prev => new Set());
  };

  const [finalUndoClicked, setFinalUndoClicked] = useState(false);
  
  const undoFinalScores = () => {
    if (finalUndoClicked) return; // Prevent multiple clicks
    
    setFinalUndoClicked(true);
    
    setTeams((prev) =>
      prev.map((team) => {
        // Only undo if there are final scores to undo
        if (team.correctAnswers?.['final']?.length > 0) {
          const pointsToRemove = team.correctAnswers['final'][0].points;
          return {
            ...team,
            score: team.score - pointsToRemove,
            correctAnswers: {
              ...team.correctAnswers,
              final: []
            }
          };
        }
        return team;
      })
    );
    
    // Remove teams from answered list when undoing
    setTeamsAnsweredCurrentQuestion(prev => new Set());
  };

  // Handle tiebreaker winner selection
  const handleTiebreakerScore = (teamId, isWinner) => {
    setTeams(prevTeams => 
      prevTeams.map(team => {
        if (team.id === teamId) {
          let scoreAdjustment = 0;
          
          // If this team is being marked as winner, add 0.5 points
          if (isWinner && !team.tiebreakerWinner) {
            scoreAdjustment = 0.5;
          }
          // If this team is being unmarked as winner, remove 0.5 points
          else if (!isWinner && team.tiebreakerWinner) {
            scoreAdjustment = -0.5;
          }
          
          return { 
            ...team, 
            tiebreakerWinner: isWinner,
            score: team.score + scoreAdjustment
          };
        }
        return team;
      })
    );
  };

  // Submit tiebreaker scores and determine final rankings
  const submitTiebreakerScores = () => {
    // Get all teams with their tiebreaker scores
    const teamsWithTiebreaker = teams.map(team => ({
      ...team,
      tiebreakerScore: team.tiebreakerScore || 0
    }));

    // Process each tie group
    tieGroups.forEach(group => {
      const groupTeams = group.teams.map(team => 
        teamsWithTiebreaker.find(t => t.id === team.id)
      ).filter(Boolean);

      // Sort teams in this group by tiebreaker score (highest first)
      const sortedGroupTeams = groupTeams.sort((a, b) => 
        Number(b.tiebreakerScore) - Number(a.tiebreakerScore)
      );

      console.log(`🏆 ${group.position} place tiebreaker results:`, 
        sortedGroupTeams.map(t => ({ name: t.name, tiebreakerScore: t.tiebreakerScore }))
      );

      // Update team rankings based on tiebreaker results
      // The winner gets the position, loser gets next position
      if (sortedGroupTeams.length >= 2) {
        const winner = sortedGroupTeams[0];
        const loser = sortedGroupTeams[1];
        
        // Update team final rankings
        setTeams(prevTeams => 
          prevTeams.map(team => {
            if (team.id === winner.id) {
              return { ...team, finalRank: group.position === '1st' ? 1 : group.position === '2nd' ? 2 : 3 };
            } else if (team.id === loser.id) {
              return { ...team, finalRank: group.position === '1st' ? 2 : group.position === '2nd' ? 3 : 4 };
            }
            return team;
          })
        );
      }
    });

    // Clear tiebreaker state and advance to final results
    setTiedTeams([]);
    setTieGroups([]);
    handleStageChange(39); // Final Score Summary stage
  };

  const undoTiebreakerScores = () => {
    setTeams((prev) =>
      prev.map((team) => {
        // Clear tiebreaker winner status and remove 0.5 points if they were a winner
        let scoreAdjustment = 0;
        if (team.tiebreakerWinner) {
          scoreAdjustment = -0.5;
        }
        
        return {
          ...team,
          tiebreakerWinner: false,
          score: team.score + scoreAdjustment
        };
      })
    );
    
    // Remove teams from answered list when undoing
    setTeamsAnsweredCurrentQuestion(prev => new Set());
  };

  // Score announcement cycling functions
  const nextTeamAnnouncement = () => {
    // Sort teams by score (lowest to highest) - start from last place, work up to first
    const sortedTeams = [...teams].sort((a, b) => Number(a.score) - Number(b.score));
    if (scoreAnnouncementIndex < sortedTeams.length - 1) {
      const newIndex = scoreAnnouncementIndex + 1;
      setScoreAnnouncementIndex(newIndex);
      
      // Force immediate localStorage update
      updateHostStage(gameId, currentStage, revealAnswer, newIndex, teams, currentSlideIndex);
      
      console.log('🎯 Next Team Announcement:', {
        currentIndex: scoreAnnouncementIndex,
        newIndex,
        totalTeams: sortedTeams.length,
        teams: sortedTeams.map(t => ({ name: t.name, score: t.score }))
      });
    }
  };

  const previousTeamAnnouncement = () => {
    if (scoreAnnouncementIndex > 0) {
      const newIndex = scoreAnnouncementIndex - 1;
      setScoreAnnouncementIndex(newIndex);
      
      // Force immediate localStorage update
      updateHostStage(gameId, currentStage, revealAnswer, newIndex, teams, currentSlideIndex);
      
      console.log('🎯 Previous Team Announcement:', {
        currentIndex: scoreAnnouncementIndex,
        newIndex,
        totalTeams: teams.length
      });
    }
  };

  const endScoreAnnouncement = () => {
    const newIndex = -1;
    setScoreAnnouncementIndex(newIndex);
    
    // Force immediate localStorage update
    updateHostStage(gameId, currentStage, revealAnswer, newIndex, teams, currentSlideIndex);
    
    console.log('🎯 End Score Announcement - currentStage:', currentStage);
    
    // Advance to next stage after score announcement
    if (currentStage === 18) {
      // After halftime, advance to quarter 3
      handleStageChange(19);
    } else if (currentStage === 32) {
      // After pre-final score update, advance to pre-final intro slides
      handleStageChange(33);
    } else if (currentStage === 39) {
      // After final score summary, advance to game over
      handleStageChange(40);
    }
  };

  // 🎯 FAN-OUT: Only write final results to Firestore
  const submitFinalResults = async () => {
    try {
      console.log('🌐 FAN-OUT: Submitting COMPREHENSIVE FINAL RESULTS to Firestore (one-time write)');
      
      // Validate required data
      if (!gameData || !teams || teams.length === 0) {
        throw new Error('Missing required game data or teams');
      }
      
      if (!user?.uid) {
        throw new Error('User authentication required');
      }
      
      // Calculate comprehensive analytics data
      const questionDifficultyAnalysis = calculateQuestionDifficulty(teams, gameData);
      const gameSessionData = calculateGameSessionData();
      
      const finalResults = {
        gameId,
        teams: teams.map(team => ({
          id: team.id,
          name: team.name,
          finalScore: team.score,
          halftimeScore: team.halftimeScore,
          finalBonusScore: team.finalScore,
          gpa: team.gpa || 0,
          usedPoints: team.usedPoints,
          // Enhanced analytics data
          questionPerformance: calculateQuestionPerformance(team, gameData),
          categoryPerformance: calculateCategoryPerformance(team, gameData),
          quarterBreakdown: calculateQuarterBreakdown(team, gameData),
          // Performance summary
          totalQuestions: calculateTotalQuestions(gameData),
          correctAnswers: calculateQuestionPerformance(team, gameData).filter(q => q.isCorrect).length,
          accuracy: calculateTotalQuestions(gameData) > 0 ? 
            (calculateQuestionPerformance(team, gameData).filter(q => q.isCorrect).length / calculateTotalQuestions(gameData)) * 100 : 0
        })),
        completedAt: serverTimestamp(),
        hostId: user?.uid || 'current-user-id',
        hostName: user?.displayName || user?.email || 'Unknown Host',
        gameData: {
          date: gameData.date,
          title: gameData.title || 'Trivia Game',
          creatorId: gameData.creatorId,
          creatorName: gameData.creatorName,
          gameType: gameData.gameType || 'standard',
          // League differentiation
          isOfficialGame: gameData.gameType === 'official' || gameData.creatorId === 'trivia-plug-official',
          isMarketplaceGame: gameData.marketplaceData ? true : false,
          isPersonalGame: !gameData.marketplaceData && gameData.creatorId !== 'trivia-plug-official'
        },
        // Enhanced venue data for analytics
        venue: {
          id: gameData.venueId,
          name: gameData.venueName,
          address: gameData.venueAddress,
          location: gameData.venueLocation,
          city: gameData.venueCity,
          state: gameData.venueState,
          country: gameData.venueCountry,
          chain: gameData.venueChain
        },
        // Comprehensive game metadata for analytics
        gameMetadata: {
          difficulty: gameData.difficulty,
          categories: gameData.categories || [],
          duration: gameData.duration,
          maxTeams: gameData.maxTeams,
          actualTeams: teams.length,
          // Enhanced game structure data
          totalQuestions: calculateTotalQuestions(gameData),
          questionTypes: analyzeQuestionTypes(gameData),
          categoryBreakdown: analyzeCategoryBreakdown(gameData),
          gameFlow: {
            stagesCompleted: currentStage,
            hadTiebreaker: tiedTeams.length > 0,
            tiebreakerParticipants: tiedTeams.length,
            gameCompleted: true
          },
          // Question difficulty analysis
          questionDifficulty: questionDifficultyAnalysis,
          // Game structure details
          hasHalftime: !!gameData.halftime,
          hasFinal: !!gameData.final,
          quarterCounts: {
            quarter1: gameData.quarter1?.length || 0,
            quarter2: gameData.quarter2?.length || 0,
            quarter3: gameData.quarter3?.length || 0,
            quarter4: gameData.quarter4?.length || 0
          }
        },
        // Game-wide performance analytics
        gameAnalytics: {
          averageScore: teams.length > 0 ? teams.reduce((sum, team) => sum + team.score, 0) / teams.length : 0,
          highestScore: teams.length > 0 ? Math.max(...teams.map(team => team.score)) : 0,
          lowestScore: teams.length > 0 ? Math.min(...teams.map(team => team.score)) : 0,
          averageGPA: teams.length > 0 ? teams.reduce((sum, team) => sum + (team.gpa || 0), 0) / teams.length : 0,
          scoreRange: teams.length > 0 ? Math.max(...teams.map(team => team.score)) - Math.min(...teams.map(team => team.score)) : 0,
          // Category performance across all teams
          categoryPerformance: (() => {
            const allCategories = {};
            teams.forEach(team => {
              const teamCategoryPerf = calculateCategoryPerformance(team, gameData);
              Object.keys(teamCategoryPerf).forEach(category => {
                if (!allCategories[category]) {
                  allCategories[category] = {
                    totalQuestions: 0,
                    totalCorrect: 0,
                    totalPoints: 0,
                    totalEarned: 0,
                    teamCount: 0
                  };
                }
                allCategories[category].totalQuestions += teamCategoryPerf[category].totalQuestions;
                allCategories[category].totalCorrect += teamCategoryPerf[category].correctAnswers;
                allCategories[category].totalPoints += teamCategoryPerf[category].totalPoints;
                allCategories[category].totalEarned += teamCategoryPerf[category].earnedPoints;
                allCategories[category].teamCount++;
              });
            });
            
            // Calculate averages
            Object.keys(allCategories).forEach(category => {
              const stats = allCategories[category];
              stats.averageAccuracy = stats.totalQuestions > 0 ? (stats.totalCorrect / stats.totalQuestions) * 100 : 0;
              stats.averagePoints = stats.teamCount > 0 ? stats.totalEarned / stats.teamCount : 0;
            });
            
            return allCategories;
                     })(),
           // Game session timing data
           sessionData: gameSessionData
         }
       };

      // Clean the data before sending to Firestore
      const cleanFinalResults = JSON.parse(JSON.stringify(finalResults));
      
      // Remove any undefined values that might cause issues
      const removeUndefined = (obj) => {
        if (obj === null || obj === undefined) return null;
        if (typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) {
          return obj.map(removeUndefined).filter(item => item !== null);
        }
        const cleaned = {};
        for (const [key, value] of Object.entries(obj)) {
          if (value !== undefined) {
            cleaned[key] = removeUndefined(value);
          }
        }
        return cleaned;
      };
      
      const firestoreSafeData = removeUndefined(cleanFinalResults);
      
      // Submit via secure Cloud Function instead of direct Firestore write
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      const submitFinalResultsFn = httpsCallable(functions, 'submitFinalResults');
      
      // Get session info for validation
      const sessionInfo = JSON.parse(localStorage.getItem(`launchSession_${gameId}`) || '{}');
      
      // Use session ID from URL parameter if available, otherwise get from current session state
      let sessionId = params.get('session') || sessionInfo.sessionId;
      
      // If no session ID found, get it from the current session state
      if (!sessionId && session?.sessionId) {
        sessionId = session.sessionId;
      }
      
      // Final fallback - this should rarely happen
      if (!sessionId) {
        throw new Error('No valid session ID found. Please try launching the game again.');
      }
      
      // Debug logging to see what session ID is being used
      console.log('🔍 DEBUG: Session ID being used for submission:', sessionId);
      console.log('🔍 DEBUG: Current session state:', session);
      console.log('🔍 DEBUG: URL params session:', params.get('session'));
      console.log('🔍 DEBUG: LocalStorage sessionInfo:', sessionInfo);
      
      const payload = {
        sessionId: sessionId,
        gameId,
        results: firestoreSafeData
      };
      
      const result = await submitFinalResultsFn(payload);
      
      if (!result.data?.ok) {
        throw new Error(result.data?.message || 'Failed to submit results');
      }
      
      console.log('✅ FAN-OUT: COMPREHENSIVE Final results submitted successfully!');
      console.log('📊 Analytics Data Collected:', {
        teams: finalResults.teams.length,
        totalQuestions: finalResults.gameMetadata.totalQuestions,
        categories: Object.keys(finalResults.gameMetadata.categoryBreakdown).length,
        questionTypes: Object.keys(finalResults.gameMetadata.questionTypes),
        averageScore: finalResults.gameAnalytics.averageScore,
        averageGPA: finalResults.gameAnalytics.averageGPA,
        hadTiebreaker: finalResults.gameMetadata.gameFlow.hadTiebreaker,
        sessionDuration: finalResults.gameAnalytics.sessionData.actualDurationMinutes
      });
      
      setGameComplete(true);
      
      // Update session status locally to reflect completion
      setSession(prevSession => ({
        ...prevSession,
        status: 'completed',
        completedAt: new Date().toISOString(),
        finalResultsSubmitted: true
      }));
      
      // Clean up local cache after successful submission
      clearGameCache(gameId);
      
      alert('Game results submitted successfully!');
      navigate('/home');
    } catch (err) {
      console.error('❌ FAN-OUT: Failed to submit results:', err);
      console.error('Error details:', {
        message: err.message,
        code: err.code,
        stack: err.stack
      });
      
      // Handle "already submitted" case - update UI to show completed status
      if (err.code === 'functions/already-exists' || err.message?.includes('already submitted')) {
        console.log('✅ Results already submitted - updating UI to show completed status');
        setGameComplete(true);
        setSession(prevSession => ({
          ...prevSession,
          status: 'completed',
          completedAt: new Date().toISOString(),
          finalResultsSubmitted: true
        }));
        alert('Game results were already submitted successfully!');
        navigate('/home');
        return;
      }
      
      // More specific error message based on error type
      let errorMessage = 'Failed to submit results. They are saved locally and will be submitted when connection is restored.';
      
      if (err.code === 'permission-denied') {
        errorMessage = 'Permission denied. You may not have write access to the database.';
      } else if (err.code === 'unavailable') {
        errorMessage = 'Database unavailable. Please check your internet connection.';
      } else if (err.code === 'unauthenticated') {
        errorMessage = 'Authentication required. Please log in again.';
      }
      
      alert(errorMessage);
    }
  };

  const handleSubmitClick = () => {
    setShowSubmitConfirmation(true);
  };

  const confirmSubmit = () => {
    setShowSubmitConfirmation(false);
    submitFinalResults();
  };

  const cancelSubmit = () => {
    setShowSubmitConfirmation(false);
  };

  // Host peek functionality - only visible to host
  const toggleHostPeek = () => {
    setHostPeekAnswer(!hostPeekAnswer);
  };

  // Audience reveal functionality - runs in RAM only
  const toggleRevealAnswer = () => {
    const newRevealState = !revealAnswer;
    setRevealAnswer(newRevealState);
    // No database writes - everything runs in RAM
  };

  // Calculate GPA for a team based on their total score out of 100 points
  const calculateTeamGPA = (team) => {
    if (!team || !gameData) return 0;
    
    // Get the team's total score
    const totalScore = team.score || 0;
    
    // Calculate the maximum possible score (100 points)
    let maxPossibleScore = 0;
    
    // Count points from all quarters
    const quarters = [gameData.quarter1, gameData.quarter2, gameData.quarter3, gameData.quarter4];
    quarters.forEach((quarter) => {
      if (quarter) {
        quarter.forEach((question) => {
          maxPossibleScore += question.points || 0;
        });
      }
    });
    
    // Add halftime points if it exists
    if (gameData.halftime) {
      maxPossibleScore += gameData.halftime.points || 0;
    }
    
    // Add final points if it exists
    if (gameData.final) {
      maxPossibleScore += gameData.final.points || 0;
    }
    
    // If no maximum score is calculated, default to 100
    if (maxPossibleScore === 0) {
      maxPossibleScore = 100;
    }
    
    // Calculate percentage score
    const percentage = totalScore / maxPossibleScore;
    
    // Convert percentage to 5.0 GPA scale
    // 100% = 5.0, 90% = 4.5, 80% = 4.0, etc.
    if (percentage >= 1.0) return 5.0;      // 100% or higher
    if (percentage >= 0.95) return 4.8;     // 95-99%
    if (percentage >= 0.90) return 4.5;     // 90-94%
    if (percentage >= 0.85) return 4.3;     // 85-89%
    if (percentage >= 0.80) return 4.0;     // 80-84%
    if (percentage >= 0.75) return 3.8;     // 75-79%
    if (percentage >= 0.70) return 3.5;     // 70-74%
    if (percentage >= 0.65) return 3.3;     // 65-69%
    if (percentage >= 0.60) return 3.0;     // 60-64%
    if (percentage >= 0.55) return 2.8;     // 55-59%
    if (percentage >= 0.50) return 2.5;     // 50-54%
    if (percentage >= 0.45) return 2.3;     // 45-49%
    return 0.0;                             // Below 45%
  };

  // Calculate detailed question performance for a team
  const calculateQuestionPerformance = (team, gameData) => {
    if (!team.usedPoints || !gameData) return [];
    
    const performance = [];
    
    // Process quarters
    const quarters = [gameData.quarter1, gameData.quarter2, gameData.quarter3, gameData.quarter4];
    quarters.forEach((quarter, quarterIndex) => {
      if (quarter) {
        quarter.forEach((question, qIndex) => {
          const usedPoints = team.usedPoints[quarterIndex + 1] || [];
          const isCorrect = usedPoints.includes(question.points);
          
          performance.push({
            quarter: quarterIndex + 1,
            questionNumber: qIndex + 1,
            category: question.category,
            questionType: question.type || 'standard',
            points: question.points,
            isCorrect: isCorrect,
            earnedPoints: isCorrect ? question.points : 0,
            questionText: question.question,
            answerText: question.answer,
            hasQuestionImage: !!question.questionImage,
            hasAnswerImage: !!question.answerImage
          });
        });
      }
    });
    
    // Add halftime question
    if (gameData.halftime) {
      const usedPoints = team.usedPoints['halftime'] || [];
      const isCorrect = usedPoints.includes(gameData.halftime.points);
      
      performance.push({
        quarter: 'halftime',
        questionNumber: 1,
        category: gameData.halftime.category,
        questionType: gameData.halftime.type || 'standard',
        points: gameData.halftime.points,
        isCorrect: isCorrect,
        earnedPoints: isCorrect ? gameData.halftime.points : 0,
        questionText: gameData.halftime.question,
        answerText: gameData.halftime.answer,
        hasQuestionImage: !!gameData.halftime.questionImage,
        hasAnswerImage: !!gameData.halftime.answerImage
      });
    }
    
    // Add final question
    if (gameData.final) {
      const usedPoints = team.usedPoints['final'] || [];
      const isCorrect = usedPoints.includes(gameData.final.points);
      
      performance.push({
        quarter: 'final',
        questionNumber: 1,
        category: gameData.final.category,
        questionType: gameData.final.type || 'standard',
        points: gameData.final.points,
        isCorrect: isCorrect,
        earnedPoints: isCorrect ? gameData.final.points : 0,
        questionText: gameData.final.question,
        answerText: gameData.final.answer,
        hasQuestionImage: !!gameData.final.questionImage,
        hasAnswerImage: !!gameData.final.answerImage
      });
    }
    
    return performance;
  };

  // Calculate category performance for a team
  const calculateCategoryPerformance = (team, gameData) => {
    const questionPerformance = calculateQuestionPerformance(team, gameData);
    const categoryStats = {};
    
    questionPerformance.forEach(q => {
      const category = q.category;
      if (!categoryStats[category]) {
        categoryStats[category] = {
          totalQuestions: 0,
          correctAnswers: 0,
          totalPoints: 0,
          earnedPoints: 0,
          accuracy: 0
        };
      }
      
      categoryStats[category].totalQuestions++;
      categoryStats[category].totalPoints += q.points;
      categoryStats[category].earnedPoints += q.earnedPoints;
      
      if (q.isCorrect) {
        categoryStats[category].correctAnswers++;
      }
    });
    
    // Calculate accuracy for each category
    Object.keys(categoryStats).forEach(category => {
      const stats = categoryStats[category];
      stats.accuracy = stats.totalQuestions > 0 ? (stats.correctAnswers / stats.totalQuestions) * 100 : 0;
    });
    
    return categoryStats;
  };

  // Calculate quarter breakdown for a team
  const calculateQuarterBreakdown = (team, gameData) => {
    const questionPerformance = calculateQuestionPerformance(team, gameData);
    const quarterStats = {
      quarter1: { totalQuestions: 0, correctAnswers: 0, totalPoints: 0, earnedPoints: 0, accuracy: 0 },
      quarter2: { totalQuestions: 0, correctAnswers: 0, totalPoints: 0, earnedPoints: 0, accuracy: 0 },
      quarter3: { totalQuestions: 0, correctAnswers: 0, totalPoints: 0, earnedPoints: 0, accuracy: 0 },
      quarter4: { totalQuestions: 0, correctAnswers: 0, totalPoints: 0, earnedPoints: 0, accuracy: 0 },
      halftime: { totalQuestions: 0, correctAnswers: 0, totalPoints: 0, earnedPoints: 0, accuracy: 0 },
      final: { totalQuestions: 0, correctAnswers: 0, totalPoints: 0, earnedPoints: 0, accuracy: 0 }
    };
    
    questionPerformance.forEach(q => {
      const quarterKey = typeof q.quarter === 'number' ? `quarter${q.quarter}` : q.quarter;
      if (quarterStats[quarterKey]) {
        quarterStats[quarterKey].totalQuestions++;
        quarterStats[quarterKey].totalPoints += q.points;
        quarterStats[quarterKey].earnedPoints += q.earnedPoints;
        
        if (q.isCorrect) {
          quarterStats[quarterKey].correctAnswers++;
        }
      }
    });
    
    // Calculate accuracy for each quarter
    Object.keys(quarterStats).forEach(quarter => {
      const stats = quarterStats[quarter];
      stats.accuracy = stats.totalQuestions > 0 ? (stats.correctAnswers / stats.totalQuestions) * 100 : 0;
    });
    
    return quarterStats;
  };

  // Calculate total questions in the game
  const calculateTotalQuestions = (gameData) => {
    let total = 0;
    
    // Count quarter questions
    const quarters = [gameData.quarter1, gameData.quarter2, gameData.quarter3, gameData.quarter4];
    quarters.forEach(quarter => {
      if (quarter) total += quarter.length;
    });
    
    // Add special questions
    if (gameData.halftime) total++;
    if (gameData.final) total++;
    
    return total;
  };

  // Analyze question types in the game
  const analyzeQuestionTypes = (gameData) => {
    const types = {};
    
    // Analyze quarters
    const quarters = [gameData.quarter1, gameData.quarter2, gameData.quarter3, gameData.quarter4];
    quarters.forEach(quarter => {
      if (quarter) {
        quarter.forEach(question => {
          const type = question.type || 'standard';
          types[type] = (types[type] || 0) + 1;
        });
      }
    });
    
    // Add special questions
    if (gameData.halftime) {
      const type = gameData.halftime.type || 'standard';
      types[type] = (types[type] || 0) + 1;
    }
    
    if (gameData.final) {
      const type = gameData.final.type || 'standard';
      types[type] = (types[type] || 0) + 1;
    }
    
    return types;
  };

  // Analyze category breakdown in the game
  const analyzeCategoryBreakdown = (gameData) => {
    const categories = {};
    
    // Analyze quarters
    const quarters = [gameData.quarter1, gameData.quarter2, gameData.quarter3, gameData.quarter4];
    quarters.forEach(quarter => {
      if (quarter) {
        quarter.forEach(question => {
          const category = question.category;
          if (category) {
            categories[category] = (categories[category] || 0) + 1;
          }
        });
      }
    });
    
    // Add special questions
    if (gameData.halftime && gameData.halftime.category) {
      categories[gameData.halftime.category] = (categories[gameData.halftime.category] || 0) + 1;
    }
    
    if (gameData.final && gameData.final.category) {
      categories[gameData.final.category] = (categories[gameData.final.category] || 0) + 1;
    }
    
    return categories;
  };

  // Calculate game-wide question difficulty analysis
  const calculateQuestionDifficulty = (teams, gameData) => {
    const questionPerformance = calculateQuestionPerformance(teams[0], gameData); // Use first team as template
    const difficultyAnalysis = [];
    
    questionPerformance.forEach(q => {
      // const questionKey = `${q.quarter}-${q.questionNumber}`; // Removed unused variable
      let correctCount = 0;
      let totalTeams = teams.length;
      
      teams.forEach(team => {
        const teamPerformance = calculateQuestionPerformance(team, gameData);
        const teamQuestion = teamPerformance.find(tq => 
          tq.quarter === q.quarter && tq.questionNumber === q.questionNumber
        );
        if (teamQuestion && teamQuestion.isCorrect) {
          correctCount++;
        }
      });
      
      difficultyAnalysis.push({
        quarter: q.quarter,
        questionNumber: q.questionNumber,
        category: q.category,
        questionType: q.questionType,
        points: q.points,
        correctCount: correctCount,
        totalTeams: totalTeams,
        successRate: totalTeams > 0 ? (correctCount / totalTeams) * 100 : 0,
        difficulty: totalTeams > 0 ? 
          (correctCount / totalTeams >= 0.8 ? 'easy' : 
           correctCount / totalTeams >= 0.4 ? 'medium' : 'hard') : 'unknown'
      });
    });
    
    return difficultyAnalysis;
  };

  // Calculate game session timing data
  const calculateGameSessionData = () => {
    try {
      const now = new Date();
      
      // Get game start time - try multiple sources
      let gameStartTime;
      if (gameData?.scheduledTime) {
        // Handle Firestore timestamp
        gameStartTime = gameData.scheduledTime.toDate ? gameData.scheduledTime.toDate() : new Date(gameData.scheduledTime);
      } else if (gameData?.createdAt) {
        // Handle Firestore timestamp
        gameStartTime = gameData.createdAt.toDate ? gameData.createdAt.toDate() : new Date(gameData.createdAt);
      } else {
        // Fallback to current time minus estimated duration
        gameStartTime = new Date(now.getTime() - (60 * 60 * 1000)); // 1 hour ago
      }
      
      // Validate the date
      if (isNaN(gameStartTime.getTime())) {
        console.warn('Invalid game start time, using fallback');
        gameStartTime = new Date(now.getTime() - (60 * 60 * 1000)); // 1 hour ago
      }
      
      const actualDuration = (now - gameStartTime) / (1000 * 60); // Duration in minutes
      
      return {
        scheduledStartTime: gameData?.scheduledTime || null,
        actualStartTime: gameStartTime.toISOString(),
        completionTime: now.toISOString(),
        actualDurationMinutes: Math.round(actualDuration * 100) / 100,
        scheduledDurationMinutes: gameData?.duration || 0,
        durationDifference: gameData?.duration ? actualDuration - gameData.duration : 0,
        // Stage progression timing
        stagesCompleted: currentStage,
        hadTiebreaker: tiedTeams.length > 0,
        tiebreakerDuration: tiedTeams.length > 0 ? 5 : 0, // Assuming 5 minutes for tiebreaker
        // Additional metadata
        gameLoadedAt: gameData?.loadedAt || null,
        firstLaunchTime: gameData?.firstLaunchTime || null
      };
    } catch (error) {
      console.error('Error calculating game session data:', error);
      // Return fallback data
      return {
        scheduledStartTime: null,
        actualStartTime: new Date().toISOString(),
        completionTime: new Date().toISOString(),
        actualDurationMinutes: 0,
        scheduledDurationMinutes: 0,
        durationDifference: 0,
        stagesCompleted: currentStage,
        hadTiebreaker: tiedTeams.length > 0,
        tiebreakerDuration: tiedTeams.length > 0 ? 5 : 0,
        error: error.message
      };
    }
  };

  // Update all teams' GPA when needed
  // const updateAllTeamsGPA = () => {
  //   setTeams(prevTeams => 
  //     prevTeams.map(team => ({
  //       ...team,
  //       gpa: calculateTeamGPA(team)
  //     }))
  //   );
  // }; // Removed unused function

  // Auto-reset audience reveal when changing stages
  const handleStageChange = (newStage) => {
    // if (gameExpired) {
    //   alert('⚠️ Cannot modify game - license has expired!');
    //   return;
    // } // Removed gameExpired check
    
    setCurrentStage(newStage);
    setCurrentSlideIndex(0); // Reset slide index when changing stages
    
    // Reset answered teams tracking when moving to a new question
    setTeamsAnsweredCurrentQuestion(new Set());
    
    // Auto-start score announcement when reaching score update stages
    if (newStage === 18 || newStage === 32 || newStage === 39) {
      // Sort teams by score (lowest to highest) and start with the team in LAST PLACE
      const sortedTeams = [...teams].sort((a, b) => Number(a.score) - Number(b.score));
      const startIndex = 0; // Start with the team in last place (lowest score)
      setScoreAnnouncementIndex(startIndex);
      
      // Force immediate localStorage update for score announcement start
      updateHostStage(newStage, revealAnswer, startIndex, teams, currentSlideIndex);
      
      const stageName = newStage === 18 ? 'Halftime' : newStage === 32 ? 'Pre-Final' : 'Final';
      console.log(`🎯 Started ${stageName} Score Announcement:`, {
        stage: newStage,
        startIndex,
        totalTeams: sortedTeams.length,
        teams: sortedTeams.map(t => ({ name: t.name, score: t.score }))
      });
    } else if (newStage === 40) {
      // Stage 40: Game Over - stay on this stage
      console.log('🎯 Stage 40: Game Over - staying on this stage');
      // No automatic progression - this is the final stage
    } else {
      // Reset audience reveal when moving to a new question
      if (revealAnswer) {
        setRevealAnswer(false);
      }
    }
    // Keep host peek state (host can still see answer for scoring)
  };

  // Check for ties and automatically advance to tiebreaker if needed
  const checkForTiesAndAdvance = () => {
    console.log('🔍 checkForTiesAndAdvance called - currentStage:', currentStage, 'teams:', teams.map(t => ({ name: t.name, score: t.score })));
    
    const sortedTeams = [...teams].sort((a, b) => Number(b.score) - Number(a.score)); // Highest to lowest
    console.log('🔍 Sorted teams for tie detection:', sortedTeams.map(t => ({ name: t.name, score: t.score })));
    
    // Check for ties in 1st, 2nd, and 3rd place
    const firstPlaceScore = sortedTeams[0]?.score || 0;
    const secondPlaceScore = sortedTeams[1]?.score || 0;
    const thirdPlaceScore = sortedTeams[2]?.score || 0;
    
    let hasTie = false;
    let allTiedTeams = [];
    let tieGroups = [];
    
    // Check for ties in top 3 positions - collect ALL ties
    if (sortedTeams.length >= 2 && firstPlaceScore === secondPlaceScore) {
      // Tie for 1st place
      hasTie = true;
      const firstPlaceTies = sortedTeams.filter(team => team.score === firstPlaceScore);
      allTiedTeams.push(...firstPlaceTies);
      tieGroups.push({
        position: '1st',
        teams: firstPlaceTies,
        score: firstPlaceScore
      });
      console.log('🏆 Tie detected for 1st place:', firstPlaceTies.map(t => t.name));
    }
    
    if (sortedTeams.length >= 3 && secondPlaceScore === thirdPlaceScore && secondPlaceScore !== firstPlaceScore) {
      // Tie for 2nd place (only if not already included in 1st place tie)
      hasTie = true;
      const secondPlaceTies = sortedTeams.filter(team => team.score === secondPlaceScore);
      allTiedTeams.push(...secondPlaceTies);
      tieGroups.push({
        position: '2nd',
        teams: secondPlaceTies,
        score: secondPlaceScore
      });
      console.log('🏆 Tie detected for 2nd place:', secondPlaceTies.map(t => t.name));
    }
    
    if (sortedTeams.length >= 4 && thirdPlaceScore === sortedTeams[3]?.score && thirdPlaceScore !== secondPlaceScore && thirdPlaceScore !== firstPlaceScore) {
      // Tie for 3rd place (only if not already included in other ties)
      hasTie = true;
      const thirdPlaceTies = sortedTeams.filter(team => team.score === thirdPlaceScore);
      allTiedTeams.push(...thirdPlaceTies);
      tieGroups.push({
        position: '3rd',
        teams: thirdPlaceTies,
        score: thirdPlaceScore
      });
      console.log('🏆 Tie detected for 3rd place:', thirdPlaceTies.map(t => t.name));
    }
    
    if (hasTie) {
      console.log('🏆 Ties detected with teams:', allTiedTeams.map(t => ({ name: t.name, score: t.score })));
      console.log('🏆 Tie groups:', tieGroups.map(g => ({ position: g.position, teams: g.teams.map(t => t.name), score: g.score })));
      
      // Set the tied teams for the tiebreaker
      console.log('🏆 Setting tiedTeams state with:', allTiedTeams.length, 'teams');
      setTiedTeams(allTiedTeams);
      // Store tie groups for reference
      setTieGroups(tieGroups);
      
      // Show tie announcement and advance to tiebreaker info stage
      setTimeout(() => {
        console.log('🏆 Advancing to stage 37 (tiebreaker info slides)');
        handleStageChange(37); // Tiebreaker Info Slides
      }, 2000); // 2 second delay to let host see the tie announcement
    } else {
      console.log('🏆 No ties detected - advancing to final score summary');
      // No ties detected, advance directly to final score summary
      setTimeout(() => {
        console.log('🏆 Advancing to stage 39 (final score summary)');
        handleStageChange(39); // Final Score Summary
      }, 2000); // 2 second delay
    }
  };

  // 🖥️ Launch Audience Display in separate window
  const launchAudienceDisplay = () => {
    const audienceUrl = `${window.location.origin}/audience/${gameId}`;
    const audienceWindow = window.open(
      audienceUrl,
      'audience-display',
      'width=1920,height=1080,scrollbars=no,resizable=yes,menubar=no,toolbar=no,location=no,status=no'
    );
    
    if (audienceWindow) {
      console.log('🖥️ Audience display launched in separate window');
      // Focus the new window
      audienceWindow.focus();
    } else {
      alert('Please allow pop-ups for this site to launch the audience display.');
    }
  };

  const generateRatingQRCode = async () => {
    try {
      // Create the rating URL
      const ratingUrl = `${window.location.origin}/rate-game/${gameId}/${user?.uid}`;
      
      // Generate QR code
      const qrDataUrl = await QRCode.toDataURL(ratingUrl, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      
      setQrCodeDataUrl(qrDataUrl);
      setShowRatingQR(true);
    } catch (error) {
      console.error('Error generating QR code:', error);
      alert('Error generating QR code. Please try again.');
    }
  };

  const closeRatingQR = () => {
    setShowRatingQR(false);
  };

  const showTeamBreakdownModal = (team) => {
    setSelectedTeam(team);
    setShowTeamBreakdown(true);
  };

  const closeTeamBreakdownModal = () => {
    setShowTeamBreakdown(false);
    setSelectedTeam(null);
    setEditingTeamName(false);
    setEditingTeamNameValue('');
    setEditingQuestion(null);
    setEditingQuestionPoints(0);
    setEditingQuestionStatus('');
  };

  const startEditingTeamName = () => {
    if (selectedTeam) {
      setEditingTeamName(true);
      setEditingTeamNameValue(selectedTeam.name);
    }
  };

  const saveTeamName = () => {
    if (selectedTeam && editingTeamNameValue.trim()) {
      setTeams(prev => prev.map(team => 
        team.id === selectedTeam.id 
          ? { ...team, name: editingTeamNameValue.trim() }
          : team
      ));
      setSelectedTeam({ ...selectedTeam, name: editingTeamNameValue.trim() });
      setEditingTeamName(false);
      setEditingTeamNameValue('');
    }
  };

  const cancelEditingTeamName = () => {
    setEditingTeamName(false);
    setEditingTeamNameValue('');
  };

  const startEditingQuestion = (quarterNumber, questionIndex, currentPoints, currentStatus) => {
    setEditingQuestion({ quarterNumber, questionIndex });
    setEditingQuestionPoints(currentPoints);
    setEditingQuestionStatus(currentStatus);
  };

  const saveQuestionEdit = () => {
    if (!editingQuestion || !selectedTeam) return;

    const { quarterNumber, questionIndex } = editingQuestion;
    
    setTeams(prev => prev.map(team => {
      if (team.id !== selectedTeam.id) return team;

      const updatedTeam = { ...team };
      
      // Remove existing entries for this question
      if (updatedTeam.usedPoints[quarterNumber]) {
        updatedTeam.usedPoints[quarterNumber] = updatedTeam.usedPoints[quarterNumber].filter(entry => 
          !(typeof entry === 'object' && entry.questionIndex === questionIndex)
        );
      }
      if (updatedTeam.correctAnswers[quarterNumber]) {
        updatedTeam.correctAnswers[quarterNumber] = updatedTeam.correctAnswers[quarterNumber].filter(entry => 
          !(typeof entry === 'object' && entry.questionIndex === questionIndex)
        );
      }
      if (updatedTeam.wrongAnswers[quarterNumber]) {
        updatedTeam.wrongAnswers[quarterNumber] = updatedTeam.wrongAnswers[quarterNumber].filter(entry => 
          !(typeof entry === 'object' && entry.questionIndex === questionIndex)
        );
      }

      // Calculate score adjustment
      let scoreAdjustment = 0;
      
      // Remove points from old entries
      const oldCorrectEntry = team.correctAnswers[quarterNumber]?.find(entry => 
        typeof entry === 'object' && entry.questionIndex === questionIndex
      );
      if (oldCorrectEntry) {
        scoreAdjustment -= oldCorrectEntry.points;
      }

      // Add new entry based on status
      if (editingQuestionStatus === 'correct' && editingQuestionPoints > 0) {
        const newEntry = { questionIndex, points: editingQuestionPoints };
        updatedTeam.usedPoints[quarterNumber] = [...(updatedTeam.usedPoints[quarterNumber] || []), newEntry];
        updatedTeam.correctAnswers[quarterNumber] = [...(updatedTeam.correctAnswers[quarterNumber] || []), newEntry];
        scoreAdjustment += editingQuestionPoints;
      } else if (editingQuestionStatus === 'wrong' && editingQuestionPoints > 0) {
        const newEntry = { questionIndex, points: editingQuestionPoints };
        updatedTeam.usedPoints[quarterNumber] = [...(updatedTeam.usedPoints[quarterNumber] || []), newEntry];
        updatedTeam.wrongAnswers[quarterNumber] = [...(updatedTeam.wrongAnswers[quarterNumber] || []), newEntry];
        // No score adjustment for wrong answers
      }

      // Update total score
      updatedTeam.score += scoreAdjustment;
      
      // Recalculate GPA
      updatedTeam.gpa = calculateTeamGPA(updatedTeam);
      
      return updatedTeam;
    }));

    // Update selectedTeam to reflect changes
    const updatedTeam = teams.find(team => team.id === selectedTeam.id);
    if (updatedTeam) {
      setSelectedTeam(updatedTeam);
    }

    // Reset editing state
    setEditingQuestion(null);
    setEditingQuestionPoints(0);
    setEditingQuestionStatus('');
  };

  const cancelEditingQuestion = () => {
    setEditingQuestion(null);
    setEditingQuestionPoints(0);
    setEditingQuestionStatus('');
  };

  const renderTeamBreakdown = () => {
    if (!selectedTeam || !gameData) return null;

    // Get the current team data from the teams state to ensure we have the latest scoring data
    const currentTeam = teams.find(team => team.id === selectedTeam.id);
    if (!currentTeam) return null;
    


    const { quarter1, quarter2, quarter3, quarter4, halftime, final } = gameData;
    const quarters = [quarter1, quarter2, quarter3, quarter4];
    
    const getTeamScoreForQuarter = (quarter, quarterIndex) => {
      if (!quarter) return 0;
      let score = 0;
      const quarterNumber = quarterIndex + 1;
      const correctAnswers = currentTeam.correctAnswers?.[quarterNumber] || [];
      correctAnswers.forEach(entry => {
        if (typeof entry === 'object' && entry.points) {
          score += entry.points;
        } else if (typeof entry === 'number') {
          score += entry;
        }
      });
      return score;
    };

    const getTeamHalftimeScore = () => {
      if (!halftime) return 0;
      const correctAnswers = currentTeam.correctAnswers?.['halftime'] || [];
      if (correctAnswers.length > 0 && correctAnswers[0].points) {
        return correctAnswers[0].points;
      }
      return 0;
    };

    const getTeamFinalScore = () => {
      if (!final) return 0;
      const correctAnswers = currentTeam.correctAnswers?.['final'] || [];
      if (correctAnswers.length > 0 && correctAnswers[0].points) {
        return correctAnswers[0].points;
      }
      return 0;
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center space-x-3">
              <h2 className="text-2xl font-bold text-gray-800">📊 </h2>
              {editingTeamName ? (
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={editingTeamNameValue}
                    onChange={(e) => setEditingTeamNameValue(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        saveTeamName();
                      } else if (e.key === 'Escape') {
                        cancelEditingTeamName();
                      }
                    }}
                    className="text-2xl font-bold text-gray-800 bg-transparent border-b-2 border-blue-500 focus:outline-none focus:border-blue-600"
                    autoFocus
                  />
                  <button
                    onClick={saveTeamName}
                    className="text-green-600 hover:text-green-700 text-lg"
                    title="Save team name"
                  >
                    ✓
                  </button>
                  <button
                    onClick={cancelEditingTeamName}
                    className="text-red-600 hover:text-red-700 text-lg"
                    title="Cancel editing"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <h2 className="text-2xl font-bold text-gray-800">{currentTeam.name} - Score Breakdown</h2>
                  <button
                    onClick={startEditingTeamName}
                    className="text-blue-600 hover:text-blue-700 text-lg"
                    title="Edit team name"
                  >
                    ✏️
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={closeTeamBreakdownModal}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ✕
            </button>
          </div>

          <div className="space-y-6">
            {/* Quarter Breakdowns */}
            {quarters.map((quarter, quarterIndex) => (
              <div key={quarterIndex} className="border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">
                  Quarter {quarterIndex + 1} - {getTeamScoreForQuarter(quarter, quarterIndex)} pts
                </h3>
                {quarter && (
                  <div className="space-y-2">
                                        {quarter.map((question, qIndex) => {
                      const quarterNumber = quarterIndex + 1;
                      const usedPoints = currentTeam.usedPoints?.[quarterNumber] || [];
                      const correctAnswers = currentTeam.correctAnswers?.[quarterNumber] || [];
                      const wrongAnswers = currentTeam.wrongAnswers?.[quarterNumber] || [];
                      
                      // Find the entry for this specific question
                      const usedEntry = usedPoints.find(entry => 
                        typeof entry === 'object' ? entry.questionIndex === qIndex : false
                      );
                      const correctEntry = correctAnswers.find(entry => 
                        typeof entry === 'object' ? entry.questionIndex === qIndex : false
                      );
                      const wrongEntry = wrongAnswers.find(entry => 
                        typeof entry === 'object' ? entry.questionIndex === qIndex : false
                      );
                      
                      const questionAnswered = !!usedEntry;
                      const gotCorrect = !!correctEntry;
                      const gotWrong = !!wrongEntry;
                      const pointsUsed = usedEntry ? (typeof usedEntry === 'object' ? usedEntry.points : usedEntry) : 0;
                      
                      const isEditing = editingQuestion && 
                        editingQuestion.quarterNumber === quarterNumber && 
                        editingQuestion.questionIndex === qIndex;

                      return (
                        <div key={qIndex} className={`flex justify-between items-center p-2 rounded ${
                          gotCorrect ? 'bg-green-50 border border-green-200' : 
                          gotWrong ? 'bg-red-50 border border-red-200' : 
                          questionAnswered ? 'bg-yellow-50 border border-yellow-200' :
                          'bg-gray-50 border border-gray-200'
                        }`}>
                          <div className="flex items-center space-x-2">
                            {isEditing ? (
                              <div className="flex flex-col space-y-1">
                                {/* Green buttons row */}
                                <div className="flex flex-row gap-0.5">
                                  {[1,2,3,4,5].map((val) => {
                                    const isUsed = currentTeam.usedPoints[quarterNumber]?.some(entry => 
                                      typeof entry === 'object' ? entry.questionIndex === qIndex && entry.points === val : false
                                    );
                                    const isWrong = currentTeam.wrongAnswers?.[quarterNumber]?.some(entry => 
                                      typeof entry === 'object' ? entry.questionIndex === qIndex && entry.points === val : false
                                    );
                                    const isCorrect = currentTeam.correctAnswers?.[quarterNumber]?.some(entry => 
                                      typeof entry === 'object' ? entry.questionIndex === qIndex && entry.points === val : false
                                    );
                                    return (
                                      <button
                                        key={val}
                                        onClick={() => {
                                          // Remove any existing entries for this question
                                          const updatedTeam = { ...currentTeam };
                                          if (updatedTeam.usedPoints[quarterNumber]) {
                                            updatedTeam.usedPoints[quarterNumber] = updatedTeam.usedPoints[quarterNumber].filter(entry => 
                                              !(typeof entry === 'object' && entry.questionIndex === qIndex)
                                            );
                                          }
                                          if (updatedTeam.correctAnswers[quarterNumber]) {
                                            updatedTeam.correctAnswers[quarterNumber] = updatedTeam.correctAnswers[quarterNumber].filter(entry => 
                                              !(typeof entry === 'object' && entry.questionIndex === qIndex)
                                            );
                                          }
                                          if (updatedTeam.wrongAnswers[quarterNumber]) {
                                            updatedTeam.wrongAnswers[quarterNumber] = updatedTeam.wrongAnswers[quarterNumber].filter(entry => 
                                              !(typeof entry === 'object' && entry.questionIndex === qIndex)
                                            );
                                          }

                                          // Calculate score adjustment
                                          let scoreAdjustment = 0;
                                          const oldCorrectEntry = currentTeam.correctAnswers[quarterNumber]?.find(entry => 
                                            typeof entry === 'object' && entry.questionIndex === qIndex
                                          );
                                          if (oldCorrectEntry) {
                                            scoreAdjustment -= oldCorrectEntry.points;
                                          }

                                          // Add new correct answer
                                          const newEntry = { questionIndex: qIndex, points: val };
                                          updatedTeam.usedPoints[quarterNumber] = [...(updatedTeam.usedPoints[quarterNumber] || []), newEntry];
                                          updatedTeam.correctAnswers[quarterNumber] = [...(updatedTeam.correctAnswers[quarterNumber] || []), newEntry];
                                          scoreAdjustment += val;

                                          // Update score and GPA
                                          updatedTeam.score += scoreAdjustment;
                                          updatedTeam.gpa = calculateTeamGPA(updatedTeam);

                                          // Update teams state
                                          setTeams(prev => prev.map(team => 
                                            team.id === currentTeam.id ? updatedTeam : team
                                          ));
                                          setSelectedTeam(updatedTeam);
                                          setEditingQuestion(null);
                                        }}
                                        className={`bg-green-600 hover:bg-green-700 text-white font-bold py-0 px-1.5 rounded text-xs shadow transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-green-400 ${isCorrect ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        disabled={isCorrect}
                                        title={`Mark as correct with ${val} points`}
                                      >
                                        +{val}
                                      </button>
                                    );
                                  })}
                                </div>
                                {/* Red X buttons row */}
                                <div className="flex flex-row gap-0.5">
                                  {[1,2,3,4,5].map((val) => {
                                    const isUsed = currentTeam.usedPoints[quarterNumber]?.some(entry => 
                                      typeof entry === 'object' ? entry.questionIndex === qIndex && entry.points === val : false
                                    );
                                    const isWrong = currentTeam.wrongAnswers?.[quarterNumber]?.some(entry => 
                                      typeof entry === 'object' ? entry.questionIndex === qIndex && entry.points === val : false
                                    );
                                    const isCorrect = currentTeam.correctAnswers?.[quarterNumber]?.some(entry => 
                                      typeof entry === 'object' ? entry.questionIndex === qIndex && entry.points === val : false
                                    );
                                    return (
                                      <button
                                        key={val}
                                        onClick={() => {
                                          // Remove any existing entries for this question
                                          const updatedTeam = { ...currentTeam };
                                          if (updatedTeam.usedPoints[quarterNumber]) {
                                            updatedTeam.usedPoints[quarterNumber] = updatedTeam.usedPoints[quarterNumber].filter(entry => 
                                              !(typeof entry === 'object' && entry.questionIndex === qIndex)
                                            );
                                          }
                                          if (updatedTeam.correctAnswers[quarterNumber]) {
                                            updatedTeam.correctAnswers[quarterNumber] = updatedTeam.correctAnswers[quarterNumber].filter(entry => 
                                              !(typeof entry === 'object' && entry.questionIndex === qIndex)
                                            );
                                          }
                                          if (updatedTeam.wrongAnswers[quarterNumber]) {
                                            updatedTeam.wrongAnswers[quarterNumber] = updatedTeam.wrongAnswers[quarterNumber].filter(entry => 
                                              !(typeof entry === 'object' && entry.questionIndex === qIndex)
                                            );
                                          }

                                          // Calculate score adjustment
                                          let scoreAdjustment = 0;
                                          const oldCorrectEntry = currentTeam.correctAnswers[quarterNumber]?.find(entry => 
                                            typeof entry === 'object' && entry.questionIndex === qIndex
                                          );
                                          if (oldCorrectEntry) {
                                            scoreAdjustment -= oldCorrectEntry.points;
                                          }

                                          // Add new wrong answer (no score adjustment)
                                          const newEntry = { questionIndex: qIndex, points: val };
                                          updatedTeam.usedPoints[quarterNumber] = [...(updatedTeam.usedPoints[quarterNumber] || []), newEntry];
                                          updatedTeam.wrongAnswers[quarterNumber] = [...(updatedTeam.wrongAnswers[quarterNumber] || []), newEntry];

                                          // Update score and GPA
                                          updatedTeam.score += scoreAdjustment;
                                          updatedTeam.gpa = calculateTeamGPA(updatedTeam);

                                          // Update teams state
                                          setTeams(prev => prev.map(team => 
                                            team.id === currentTeam.id ? updatedTeam : team
                                          ));
                                          setSelectedTeam(updatedTeam);
                                          setEditingQuestion(null);
                                        }}
                                        className={`bg-red-600 hover:bg-red-700 text-white font-bold py-0 px-1.5 rounded text-xs shadow transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-red-400 ${isWrong ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        disabled={isWrong}
                                        title={`Mark as wrong with ${val} points`}
                                      >
                                        X
                                      </button>
                                    );
                                  })}
                                </div>
                                {/* Undo buttons row */}
                                <div className="flex flex-row gap-0.5">
                                  {[1,2,3,4,5].map((val) => (
                                    <button
                                      key={val}
                                      onClick={() => {
                                        const updatedTeam = { ...currentTeam };
                                        
                                        // Check if there are points to undo
                                        const hasCorrectAnswer = (updatedTeam.correctAnswers?.[quarterNumber] || []).some(entry => 
                                          typeof entry === 'object' && entry.questionIndex === qIndex && entry.points === val
                                        );
                                        const hasWrongAnswer = (updatedTeam.wrongAnswers?.[quarterNumber] || []).some(entry => 
                                          typeof entry === 'object' && entry.questionIndex === qIndex && entry.points === val
                                        );
                                        
                                        if (!hasCorrectAnswer && !hasWrongAnswer) {
                                          setEditingQuestion(null);
                                          return;
                                        }
                                        
                                        // Remove entries for this question and point value
                                        if (updatedTeam.usedPoints[quarterNumber]) {
                                          updatedTeam.usedPoints[quarterNumber] = updatedTeam.usedPoints[quarterNumber].filter(entry => 
                                            !(typeof entry === 'object' && entry.questionIndex === qIndex && entry.points === val)
                                          );
                                        }
                                        if (updatedTeam.correctAnswers[quarterNumber]) {
                                          updatedTeam.correctAnswers[quarterNumber] = updatedTeam.correctAnswers[quarterNumber].filter(entry => 
                                            !(typeof entry === 'object' && entry.questionIndex === qIndex && entry.points === val)
                                          );
                                        }
                                        if (updatedTeam.wrongAnswers[quarterNumber]) {
                                          updatedTeam.wrongAnswers[quarterNumber] = updatedTeam.wrongAnswers[quarterNumber].filter(entry => 
                                            !(typeof entry === 'object' && entry.questionIndex === qIndex && entry.points === val)
                                          );
                                        }
                                        
                                        // Only subtract points if there was originally a correct answer
                                        let scoreAdjustment = 0;
                                        if (hasCorrectAnswer) {
                                          scoreAdjustment -= val;
                                        }
                                        
                                        // Update score and GPA
                                        updatedTeam.score += scoreAdjustment;
                                        updatedTeam.gpa = calculateTeamGPA(updatedTeam);
                                        
                                        // Update teams state
                                        setTeams(prev => prev.map(team => 
                                          team.id === currentTeam.id ? updatedTeam : team
                                        ));
                                        setSelectedTeam(updatedTeam);
                                        setEditingQuestion(null);
                                      }}
                                      className="bg-yellow-400 hover:bg-yellow-500 text-white rounded font-bold text-xs shadow-sm transition-all duration-200 transform hover:scale-105 py-0 px-1.5"
                                      title={`Undo ${val} points`}
                                    >
                                      ↺
                                    </button>
                                  ))}
                                </div>
                                <button
                                  onClick={cancelEditingQuestion}
                                  className="text-red-600 hover:text-red-700 text-xs mt-1"
                                  title="Cancel editing"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <>
                                <span className={`text-sm font-medium ${
                                  gotCorrect ? 'text-green-700' : 
                                  gotWrong ? 'text-red-700' : 
                                  questionAnswered ? 'text-yellow-700' :
                                  'text-gray-500'
                                }`}>
                                  {gotCorrect ? '✅' : gotWrong ? '❌' : questionAnswered ? '⚠️' : '⭕'}
                                </span>
                                <span className="text-sm text-gray-700">
                                  Q{qIndex + 1}: {question.category}
                                </span>
                                <button
                                  onClick={() => startEditingQuestion(quarterNumber, qIndex, pointsUsed, gotCorrect ? 'correct' : gotWrong ? 'wrong' : 'none')}
                                  className="text-blue-600 hover:text-blue-700 text-sm ml-2"
                                  title="Edit question score"
                                >
                                  ✏️
                                </button>
                              </>
                            )}
                          </div>
                          {!isEditing && (
                            <span className={`text-sm font-bold ${
                              gotCorrect ? 'text-green-700' : 
                              gotWrong ? 'text-red-700' : 
                              questionAnswered ? 'text-yellow-700' :
                              'text-gray-500'
                            }`}>
                              {gotCorrect ? `+${pointsUsed}` : gotWrong ? `-${pointsUsed}` : questionAnswered ? `±${pointsUsed}` : `+0`} pts
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}

            {/* Halftime */}
            {halftime && (
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">
                  Halftime - {getTeamHalftimeScore()} pts
                </h3>
                <div className={`flex justify-between items-center p-2 rounded ${
                  currentTeam.halftimeScore !== undefined ? 'bg-blue-50 border border-blue-200' : 
                  'bg-gray-50 border border-gray-200'
                }`}>
                  <div className="flex items-center space-x-2">
                    <span className={`text-sm font-medium ${
                      currentTeam.halftimeScore !== undefined ? 'text-blue-700' : 
                      'text-gray-500'
                    }`}>
                      {currentTeam.halftimeScore !== undefined ? '📝' : '⭕'}
                    </span>
                    <span className="text-sm text-gray-700">
                      Halftime: {halftime.category}
                    </span>
                  </div>
                  <span className={`text-sm font-bold ${
                    currentTeam.halftimeScore !== undefined ? 'text-blue-700' : 
                    'text-gray-500'
                  }`}>
                    {currentTeam.halftimeScore !== undefined ? `+${currentTeam.halftimeScore}` : `+0`} pts
                  </span>
                </div>
              </div>
            )}

            {/* Final Question */}
            {final && (
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">
                  Final Question - {getTeamFinalScore()} pts
                </h3>
                <div className={`flex justify-between items-center p-2 rounded ${
                  currentTeam.finalScore !== undefined ? 'bg-purple-50 border border-purple-200' : 
                  'bg-gray-50 border border-gray-200'
                }`}>
                  <div className="flex items-center space-x-2">
                    <span className={`text-sm font-medium ${
                      currentTeam.finalScore !== undefined ? 'text-purple-700' : 
                      'text-gray-500'
                    }`}>
                      {currentTeam.finalScore !== undefined ? '📝' : '⭕'}
                    </span>
                    <span className="text-sm text-gray-700">
                      Final: {final.category}
                    </span>
                  </div>
                  <span className={`text-sm font-bold ${
                    currentTeam.finalScore !== undefined ? 'text-purple-700' : 
                    'text-gray-500'
                  }`}>
                    {currentTeam.finalScore !== undefined ? `+${currentTeam.finalScore}` : `+0`} pts
                  </span>
                </div>
              </div>
            )}

            {/* Total Score */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-gray-800">Total Score</h3>
                <span className="text-2xl font-bold text-blue-600">{currentTeam.score} pts</span>
              </div>
              <div className="mt-2 text-sm text-gray-600">
                <p>This breakdown shows exactly how {currentTeam.name} earned their score.</p>
                <p>Each question shows whether they got it correct (✅) or incorrect (❌) and the points awarded.</p>
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={closeTeamBreakdownModal}
              className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderStageContent = () => {
    if (!gameData) return <p className="text-gray-600">Loading game data...</p>;

    const {
      quarter1, quarter2, quarter3, quarter4,
      halftime, final, tieBreaker
    } = gameData;

    const getQA = (quarter, index) => quarter && quarter[index];

    const renderCategoryOverview = (quarter, label) => {
      // TEMP DEBUG: Show raw quarter array and categories
      console.log('DEBUG renderCategoryOverview:', label, quarter);
      if (!quarter) return <p className="text-red-500">No {label} data.</p>;
      return (
        <div className="bg-gray-900 border border-gray-700 rounded p-6 shadow">
          <h3 className="text-2xl font-bold text-white mb-6">{label}</h3>
          <ul className="space-y-3">
            {quarter.map((q, idx) => (
              <li key={idx} className="text-lg text-white font-semibold flex items-center gap-3">
                <span className="bg-gray-700 text-green-300 rounded-full w-8 h-8 flex items-center justify-center font-bold mr-2">Q{idx + 1}</span>
                <span>{q?.category || <span className='text-red-400'>N/A</span>}</span>
              </li>
            ))}
          </ul>
        </div>
      );
    };



    const renderSlide = (slide) => {
      if (!slide) return <p className="text-gray-500">No slide content available.</p>;
      
      const isPreGame = [1, 2, 3, 4].includes(currentStage);
      
      // Check if content is just a simple title (short word or phrase)
      const isSimpleTitle = slide.content && 
        ((slide.content.length <= 20 && slide.content.split(' ').length <= 3) ||
        slide.content.toLowerCase() === 'rules' ||
        slide.content.toLowerCase().includes('how ') ||
        slide.content.toLowerCase().includes('work'));
      
      return (
        <div className="text-center">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-8 rounded-3xl shadow-2xl border border-gray-700 max-w-4xl mx-auto">
            {!isSimpleTitle && (
              <div className="mb-6">
                <p className="text-xl text-gray-300 leading-relaxed">{slide.content}</p>
              </div>
            )}
            
            {slide.imageUrl && (
              <div className="mt-6">
                <img 
                  src={slide.imageUrl} 
                  alt="Slide visual" 
                  className="max-h-96 mx-auto rounded-2xl shadow-xl" 
                />
              </div>
            )}
            
            <div className="mt-6 text-sm text-gray-400">
                                  <p>📢 Slide {currentSlideIndex + 1} of {getSlidesForCurrentStage.length}</p>
            </div>

            {/* Begin Game Button for Pre-Game Slides */}
                            {isPreGame && currentSlideIndex === getSlidesForCurrentStage.length - 1 && (
              <div className="mt-8">
                <button
                  onClick={() => handleStageChange(getNextStage(currentStage))}
                  className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-8 py-4 rounded-2xl font-bold text-xl shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105"
                >
                  🎮 Begin Game
                </button>
                <p className="text-sm text-gray-400 mt-2">
                  Click to advance to the next stage
                </p>
              </div>
            )}
          </div>
        </div>
      );
    };

    const renderScoreUpdate = (title, isFinal = false) => {
      // For host display: sort by score (lowest to highest) to match announcement logic
      const sortedTeams = [...teams].sort((a, b) => Number(a.score) - Number(b.score));
      
      // Function to calculate proper rank display that handles ties
      const getRankDisplay = (teamIndex) => {
        const team = sortedTeams[teamIndex];
        if (!team) return '#?';
        
        // Calculate the actual rank (teams are sorted lowest to highest for announcement)
        // So teamIndex 0 = last place, teamIndex teams.length-1 = first place
        const actualRank = teams.length - teamIndex;
        
        // Check if this team is tied with others
        const teamsWithSameScore = sortedTeams.filter(t => t.score === team.score);
        
        if (teamsWithSameScore.length === 1) {
          // No tie - show individual rank
          let rankText;
          if (actualRank === 1) rankText = '1st';
          else if (actualRank === 2) rankText = '2nd';
          else if (actualRank === 3) rankText = '3rd';
          else rankText = `${actualRank}th`;
          return `#${rankText}`;
        } else {
          // Tie - show "Tied for X"
          let rankText;
          if (actualRank === 1) rankText = '1st';
          else if (actualRank === 2) rankText = '2nd';
          else if (actualRank === 3) rankText = '3rd';
          else rankText = `${actualRank}th`;
          return `Tied for ${rankText}`;
        }
      };
      
      // Check for ties in final score summary
      let tieInfo = null;
      if (isFinal && teams.length > 0) {
        const highestToLowest = [...teams].sort((a, b) => Number(b.score) - Number(a.score)); // Highest to lowest
        const firstPlaceScore = highestToLowest[0]?.score || 0;
        const secondPlaceScore = highestToLowest[1]?.score || 0;
        const thirdPlaceScore = highestToLowest[2]?.score || 0;
        
        // Check for ties in top 3 positions
        if (highestToLowest.length >= 2 && firstPlaceScore === secondPlaceScore) {
          // Tie for 1st place
          tieInfo = {
            type: '1st',
            teams: highestToLowest.filter(team => team.score === firstPlaceScore),
            score: firstPlaceScore
          };
        } else if (highestToLowest.length >= 3 && secondPlaceScore === thirdPlaceScore) {
          // Tie for 2nd place
          tieInfo = {
            type: '2nd',
            teams: highestToLowest.filter(team => team.score === secondPlaceScore),
            score: secondPlaceScore
          };
        } else if (highestToLowest.length >= 4 && thirdPlaceScore === highestToLowest[3]?.score) {
          // Tie for 3rd place
          tieInfo = {
            type: '3rd',
            teams: highestToLowest.filter(team => team.score === thirdPlaceScore),
            score: thirdPlaceScore
          };
        }
      }
      
      console.log('🏆 Host Score Display Debug:', {
        title,
        originalTeams: teams.map(t => ({ name: t.name, score: t.score, scoreType: typeof t.score })),
        sortedTeams: sortedTeams.map(t => ({ name: t.name, score: t.score, scoreType: typeof t.score })),
        rankings: sortedTeams.map((team, index) => ({
          rank: index + 1,
          name: team.name,
          score: team.score,
          scoreType: typeof team.score
        })),
        tieInfo
      });
      const isHalftime = title.includes('Halftime');
      const isPreFinal = title.includes('Pre-Final');
      const isFinalScore = title.includes('Final') && !title.includes('Pre-Final');
      
      // For score announcement stages, always show cycling interface
      if (isHalftime || isPreFinal || isFinalScore) {
        // If we're in score announcement mode, show only the current team
        if (scoreAnnouncementIndex >= 0) {
          const currentTeam = sortedTeams[scoreAnnouncementIndex];
          if (!currentTeam) return null;
          
          // Calculate the correct rank (last place = lowest score, first place = highest score)
          const correctRank = teams.length - scoreAnnouncementIndex;
          
          // Determine styling based on stage
          let bgGradient, borderColor, textColor, emoji, stageName;
          if (isHalftime) {
            bgGradient = "from-blue-50 to-blue-100";
            borderColor = "border-blue-200";
            textColor = "text-blue-800";
            emoji = "🏈";
            stageName = "Halftime Score Update";
          } else if (isPreFinal) {
            bgGradient = "from-purple-50 to-purple-100";
            borderColor = "border-purple-200";
            textColor = "text-purple-800";
            emoji = "📊";
            stageName = "Pre-Final Score Update";
          } else if (isFinalScore) {
            bgGradient = tieInfo ? "from-yellow-50 to-orange-100" : "from-green-50 to-green-100";
            borderColor = tieInfo ? "border-yellow-200" : "border-green-200";
            textColor = tieInfo ? "text-yellow-800" : "text-green-800";
            emoji = tieInfo ? "⚡" : "🏆";
            stageName = tieInfo ? "Final Score Summary - TIE DETECTED!" : "Final Score Summary";
          }
          
          return (
            <div className="text-center">
              <div className={`bg-gradient-to-br ${bgGradient} p-8 rounded-3xl shadow-2xl border ${borderColor} max-w-4xl mx-auto`}>
                <h3 className={`text-3xl font-bold ${textColor} mb-6`}>{emoji} {stageName}</h3>
                
                {/* Tie Information Display */}
                {tieInfo && (
                  <div className="mb-6 p-4 bg-gradient-to-r from-yellow-100 to-orange-100 rounded-xl border-2 border-yellow-300">
                    <div className="text-center">
                      <div className="text-xl font-bold text-yellow-800 mb-2">
                        🏆 TIE FOR {tieInfo.type.toUpperCase()} PLACE! 🏆
                      </div>
                      <div className="text-lg text-yellow-700 mb-2">
                        {tieInfo.teams.length} teams tied with {tieInfo.score} points
                      </div>
                      <div className="text-sm text-yellow-600">
                        {tieInfo.teams.map(team => team.name).join(' • ')}
                      </div>
                      <div className="mt-2 text-yellow-700 font-semibold">
                        ⚡ Tiebreaker round will begin automatically! ⚡
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Progress indicator */}
                <div className={`mb-6 text-sm ${isHalftime ? 'text-blue-600' : isPreFinal ? 'text-purple-600' : tieInfo ? 'text-yellow-600' : 'text-green-600'}`}>
                  Team {scoreAnnouncementIndex + 1} of {sortedTeams.length} • {teams.length - scoreAnnouncementIndex} teams remaining
                </div>
                
                {/* Current Team Display */}
                <div className={`bg-white rounded-2xl p-8 shadow-lg border ${isHalftime ? 'border-blue-300' : isPreFinal ? 'border-purple-300' : tieInfo ? 'border-yellow-300' : 'border-green-300'}`}>
                  <div className="text-center mb-6">
                    <div className={`text-6xl font-bold mb-4 ${isHalftime ? 'text-blue-600' : isPreFinal ? 'text-purple-600' : tieInfo ? 'text-yellow-600' : 'text-green-600'}`}>
                      {getRankDisplay(scoreAnnouncementIndex)}
                    </div>
                    <div className="text-4xl font-bold text-gray-800 mb-2">
                      {currentTeam.name}
                    </div>
                    <div className="text-2xl text-gray-600">
                      Current Score: <span className="font-bold text-green-600">{currentTeam.score} pts</span>
                    </div>
                    {isFinalScore && currentTeam.gpa !== undefined && (
                      <div className="text-xl text-gray-500 mt-2">
                        GPA: <span className="font-bold text-blue-600">{currentTeam.gpa.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Navigation Controls */}
                  <div className="flex justify-center space-x-4">
                    <button
                      onClick={previousTeamAnnouncement}
                      disabled={scoreAnnouncementIndex === 0}
                      className="bg-gray-500 hover:bg-gray-600 disabled:bg-gray-300 text-white px-6 py-3 rounded-lg font-semibold transition-colors disabled:cursor-not-allowed"
                    >
                      ⬅️ Previous Team
                    </button>
                    
                    <button
                      onClick={nextTeamAnnouncement}
                      disabled={scoreAnnouncementIndex === sortedTeams.length - 1}
                      className={`${isHalftime ? 'bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300' : isPreFinal ? 'bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300' : tieInfo ? 'bg-yellow-600 hover:bg-yellow-700 disabled:bg-yellow-300' : 'bg-green-600 hover:bg-green-700 disabled:bg-green-300'} text-white px-6 py-3 rounded-lg font-semibold transition-colors disabled:cursor-not-allowed`}
                    >
                      Next Team ➡️
                    </button>
                  </div>
                  
                  {/* End Announcement Button */}
                  <div className="mt-4">
                    <button
                      onClick={endScoreAnnouncement}
                      className="bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
                    >
                      End Announcement
                    </button>
                  </div>
                  
                  {/* Final Results Buttons - Only show for final score summary */}
                  {isFinalScore && (
                    <div className="mt-6 space-y-3">
                      <div className="flex justify-center space-x-4">
                        <button
                          onClick={handleSubmitClick}
                          className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                        >
                          📊 Submit Final Results
                        </button>
                        
                        <button
                          onClick={generateRatingQRCode}
                          className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                        >
                          📱 Show Rating QR Code
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        }
      }
      
      // For final scores, show manual scoring interface
      if (isFinalScore) {
        return (
          <div className="text-center">
            <div className="bg-gradient-to-br from-white to-gray-50 p-8 rounded-3xl shadow-2xl border border-gray-200 max-w-4xl mx-auto">
              <h3 className="text-3xl font-bold text-gray-800 mb-6">{title}</h3>
              
              {/* Manual Scoring Instructions */}
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="text-lg font-semibold text-blue-800 mb-2">
                  🎯 Final Wager Scoring
                </h4>
                <p className="text-blue-700 text-sm">
                  Enter wager results: positive for correct answers, negative for incorrect wagers
                </p>
              </div>
              
              <div className="space-y-4">
                {sortedTeams.map((team, index) => (
                  <div key={team.id} className="flex justify-between items-center p-4 bg-white rounded-xl shadow-md border border-gray-200">
                    <div className="flex items-center space-x-4">
                      <span className="text-2xl font-bold text-gray-600">#{index + 1}</span>
                      <span className="text-xl font-semibold text-gray-800">{team.name}</span>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      {/* Current Score */}
                      <div className="text-right">
                        <span className="text-2xl font-bold text-green-600">{team.score} pts</span>
                        {isFinal && team.gpa && (
                          <div className="text-sm text-gray-500">GPA: {team.gpa}</div>
                        )}
                      </div>
                      
                      {/* Manual Score Input */}
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-600">
                          Wager:
                        </span>
                        <input
                          type="number"
                          value={team.finalScore || ''}
                          onChange={(e) => handleFinalScore(team.id, e.target.value)}
                          className="w-20 p-2 border border-gray-300 rounded text-center font-semibold"
                          placeholder="0"
                        />
                        <span className="text-sm text-gray-500">pts</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Action Buttons */}
              <div className="mt-6 flex justify-center space-x-4">
                <button
                  onClick={submitFinalScores}
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  ✅ Submit Final Scores
                </button>
                <button
                  onClick={undoFinalScores}
                  className="bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  ↺ Undo Final Scores
                </button>
              </div>
            </div>
          </div>
        );
      }
      
      // For other score updates (pre-final, etc.), show simple display
      return (
        <div className="text-center">
          <div className="bg-gradient-to-br from-white to-gray-50 p-8 rounded-3xl shadow-2xl border border-gray-200 max-w-4xl mx-auto">
            <h3 className="text-3xl font-bold text-gray-800 mb-6">{title}</h3>
            
            <div className="space-y-4">
              {sortedTeams.map((team, index) => (
                <div key={team.id} className="flex justify-between items-center p-4 bg-white rounded-xl shadow-md border border-gray-200">
                  <div className="flex items-center space-x-4">
                    <span className="text-2xl font-bold text-gray-600">#{index + 1}</span>
                    <span className="text-xl font-semibold text-gray-800">{team.name}</span>
                  </div>
                  
                  <div className="text-right">
                    <span className="text-2xl font-bold text-green-600">{team.score} pts</span>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-6 text-sm text-gray-500">
              <p>Click "Next" to continue</p>
            </div>
          </div>
        </div>
      );
    };



    switch (currentStage) {
      // Pre-Game Slides
      case 1: 
        const pregameSlides = getSlidesForCurrentStage;
        return pregameSlides.length > 0 && currentSlideIndex < pregameSlides.length 
          ? renderSlide(pregameSlides[currentSlideIndex])
          : <p className="text-gray-500">No pre-game slides available.</p>;
      
      // Quarter 1
      case 2: return renderCategoryOverview(quarter1, "Quarter 1 Categories");
      case 3: case 4: case 5: case 6: case 7:
        return renderQA(getQA(quarter1, currentStage - 3));
      
      // Quarter 2
      case 8: return renderCategoryOverview(quarter2, "Quarter 2 Categories");
      case 9: case 10: case 11: case 12: case 13:
        return renderQA(getQA(quarter2, currentStage - 9));
      
      // Halftime Slides
      case 14: 
        const halftimeSlides = getSlidesForCurrentStage;
        return halftimeSlides.length > 0 && currentSlideIndex < halftimeSlides.length 
          ? renderSlide(halftimeSlides[currentSlideIndex])
          : <p className="text-gray-500">No halftime rules available.</p>;
      
      case 15: return renderCategoryOverview([halftime], "Halftime Category");
      case 16: return renderQA(halftime);
      
      case 17: 
        const halftimeAdsSlides = getSlidesForCurrentStage;
        return halftimeAdsSlides.length > 0 && currentSlideIndex < halftimeAdsSlides.length 
          ? renderSlide(halftimeAdsSlides[currentSlideIndex])
          : <p className="text-gray-500">No halftime ads available.</p>;
      
      case 18: return renderScoreUpdate("Halftime Score Update");
      
      // Quarter 3
      case 19: return renderCategoryOverview(quarter3, "Quarter 3 Categories");
      case 20: case 21: case 22: case 23: case 24:
        return renderQA(getQA(quarter3, currentStage - 20));
      
      // Quarter 4
      case 25: return renderCategoryOverview(quarter4, "Quarter 4 Categories");
      case 26: case 27: case 28: case 29: case 30:
        return renderQA(getQA(quarter4, currentStage - 26));
      
      // Pre-Final Slides
      case 31: 
        const preFinalAdsSlides = getSlidesForCurrentStage;
        return preFinalAdsSlides.length > 0 && currentSlideIndex < preFinalAdsSlides.length 
          ? renderSlide(preFinalAdsSlides[currentSlideIndex])
          : <p className="text-gray-500">No pre-final ads available.</p>;
      
      case 32: 
        return renderScoreUpdate("Pre-Final Score Update");
      
      case 33: 
        const preFinalSlides = getSlidesForCurrentStage;
        return preFinalSlides.length > 0 && currentSlideIndex < preFinalSlides.length 
          ? renderSlide(preFinalSlides[currentSlideIndex])
          : <p className="text-gray-500">No pre-final slides available.</p>;
      
      // Final
      case 34: return renderCategoryOverview([final], "Final Category");
      case 35: return renderQA(final);
      case 36: {
        // Check for ties immediately when on this stage
        const sortedTeams = [...teams].sort((a, b) => Number(b.score) - Number(a.score));
        const firstPlaceScore = sortedTeams[0]?.score || 0;
        const secondPlaceScore = sortedTeams[1]?.score || 0;
        const thirdPlaceScore = sortedTeams[2]?.score || 0;
        
        let hasTie = false;
        let tieInfo = null;
        
        // Check for ties in top 3 positions
        if (sortedTeams.length >= 2 && firstPlaceScore === secondPlaceScore) {
          hasTie = true;
          tieInfo = {
            type: '1st',
            teams: sortedTeams.filter(team => team.score === firstPlaceScore),
            score: firstPlaceScore
          };
        } else if (sortedTeams.length >= 3 && secondPlaceScore === thirdPlaceScore) {
          hasTie = true;
          tieInfo = {
            type: '2nd',
            teams: sortedTeams.filter(team => team.score === secondPlaceScore),
            score: secondPlaceScore
          };
        } else if (sortedTeams.length >= 4 && thirdPlaceScore === sortedTeams[3]?.score) {
          hasTie = true;
          tieInfo = {
            type: '3rd',
            teams: sortedTeams.filter(team => team.score === thirdPlaceScore),
            score: thirdPlaceScore
          };
        }
        
        return (
          <div className="text-center">
            <div className="bg-gradient-to-r from-blue-900 to-purple-900 p-8 rounded-2xl border border-blue-600 mb-6">
              <div className="flex items-center justify-center space-x-4">
                <span className="text-blue-400 text-4xl">🔍</span>
                <h2 className="text-3xl font-bold text-blue-200">
                  CHECKING FOR TIES
                </h2>
                <span className="text-blue-400 text-4xl">🔍</span>
              </div>
              
              {hasTie ? (
                <div className="mt-6">
                  <div className="bg-gradient-to-r from-yellow-900 to-orange-900 p-6 rounded-xl border-2 border-yellow-500 mb-6">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-yellow-300 mb-4">
                        🏆 TIE FOR {tieInfo.type.toUpperCase()} PLACE! 🏆
                      </div>
                      <div className="text-xl text-yellow-200 mb-4">
                        {tieInfo.teams.length} teams tied with {tieInfo.score} points
                      </div>
                      <div className="text-lg text-yellow-100 mb-4">
                        {tieInfo.teams.map(team => team.name).join(' • ')}
                      </div>
                      <div className="text-yellow-300 font-semibold">
                        ⚡ Tiebreaker round will begin! ⚡
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={checkForTiesAndAdvance}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                  >
                    Continue to Tiebreaker
                  </button>
                </div>
              ) : (
                <div className="mt-6">
                  <div className="bg-gradient-to-r from-green-900 to-emerald-900 p-6 rounded-xl border-2 border-green-500 mb-6">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-300 mb-4">
                        ✅ NO TIES DETECTED
                      </div>
                      <div className="text-xl text-green-200">
                        All teams have different scores. Proceeding to final results.
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={checkForTiesAndAdvance}
                    className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                  >
                    Continue to Final Results
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      }
      case 37: 
        const tiebreakerSlides = getSlidesForCurrentStage;
        return tiebreakerSlides.length > 0 && currentSlideIndex < tiebreakerSlides.length 
          ? renderSlide(tiebreakerSlides[currentSlideIndex])
          : <p className="text-gray-500">No tiebreaker info available.</p>;
      
      case 38: return renderQA(tieBreaker);
      case 39: return renderScoreUpdate("Final Score Summary", true);
      case 40: return (
        <div className="text-center">
          <div className="mb-12">
            <h2 className="text-8xl font-bold mb-8 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              🎉 Game Over
            </h2>
            <div className="w-48 h-2 bg-gradient-to-r from-purple-500 to-pink-500 mx-auto rounded-full"></div>
          </div>
          
          {/* Final Results - All Teams */}
          <div className="bg-gradient-to-br from-purple-900 to-pink-900 p-12 rounded-3xl shadow-2xl border border-purple-700 mb-8">
            <h3 className="text-5xl font-bold text-white mb-8">🏆 Final Results</h3>
            
            {teams.length > 0 ? (
              <div className="space-y-6">
                {/* Sort teams by score (highest to lowest) */}
                {[...teams].sort((a, b) => Number(b.score) - Number(a.score)).map((team, index) => (
                  <div key={team.name} className="bg-gradient-to-br from-gray-800 to-gray-900 p-6 rounded-2xl border border-gray-600">
                    <div className="flex items-center justify-between">
                      {/* Rank and Team Name */}
                      <div className="flex items-center space-x-6">
                        <div className="text-6xl font-bold text-purple-400">
                          #{index + 1}
                        </div>
                        <div className="text-left">
                          <div className="text-4xl font-bold text-white mb-2">
                            {team.name}
                          </div>
                          <div className="text-2xl text-gray-300">
                            Score: <span className="font-bold text-yellow-400">{team.score} pts</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* GPA */}
                      <div className="text-right">
                        <div className="text-3xl font-bold text-blue-300">
                          GPA: {team.gpa !== undefined ? team.gpa.toFixed(2) : 'N/A'}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-3xl text-gray-400">
                No teams participated in this game.
              </div>
            )}
          </div>
          
          {/* Thank You Message */}
          <div className="bg-gradient-to-br from-purple-900 to-pink-900 p-8 rounded-3xl shadow-2xl border border-purple-700">
            <div className="text-6xl mb-4">🎉</div>
            <p className="text-3xl text-gray-300 font-medium">Thank you for playing!</p>
          </div>
        </div>
      );
      
      default:
        return <p className="text-gray-500">No content for this stage.</p>;
    }
  };

  // const renderFinalSummary = () => {
  //   const sortedTeams = [...teams].sort((a, b) => Number(b.score) - Number(a.score));
  //   return (
  //     <div className="bg-white border rounded p-4 shadow">
  //       <h3 className="text-lg font-bold mb-4">Final Results</h3>
  //       <div className="space-y-2">
  //         {sortedTeams.map((team, index) => (
  //           <div key={team.id} className="flex justify-between items-center p-2 bg-gray-50 rounded">
  //             <span className="font-semibold">
  //               {index + 1}. {team.name}
  //             </span>
  //             <span className="text-lg font-bold">{team.score} pts</span>
  //           </div>
  //         ))}
  //       </div>
  //       <button
  //         onClick={handleSubmitClick}
  //         className="mt-4 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
  //       >
  //         Submit Final Results
  //       </button>
  //     </div>
  //   );
  // }; // Removed unused function

  const renderQA = (qa) => {
    if (!qa) return <p className="text-red-500">Missing question data.</p>;
    return (
      <div className="text-center">
        <div className="mb-8">
          <h3 className="text-3xl font-bold text-white mb-6 bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
            {qa.category || 'N/A'}
          </h3>
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-8 rounded-3xl shadow-2xl border border-gray-700">
            <div className="mb-6">
              {qa.type === 'matching' ? (
                <div className="space-y-6">
                  {/* Matching Question Instruction */}
                  <div className="mb-8">
                    <p className="text-3xl font-bold text-white leading-relaxed">
                      {qa.questionInstruction || 'Match the items below'}
                    </p>
                  </div>
                  
                  {/* Matching Columns */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Left Column */}
                    <div className="bg-gray-700 rounded-2xl p-6 border border-gray-600">
                      <h4 className="text-xl font-bold text-blue-300 mb-4">Left Column</h4>
                      <div className="space-y-3 text-left">
                        {qa.leftColumn?.split('\n').map((item, index) => (
                          <div key={index} className="text-lg text-white font-semibold">
                            {item.trim()}
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Right Column */}
                    <div className="bg-gray-700 rounded-2xl p-6 border border-gray-600">
                      <h4 className="text-xl font-bold text-green-300 mb-4">Right Column</h4>
                      <div className="space-y-3 text-left">
                        {qa.rightColumn?.split('\n').map((item, index) => (
                          <div key={index} className="text-lg text-white font-semibold">
                            {item.trim()}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-2xl font-semibold text-white leading-relaxed">{qa.question}</p>
              )}
            </div>
            {qa.questionImage && (
              <div className="mb-6">
                <img 
                  src={qa.questionImage} 
                  alt="Question Visual" 
                  className="max-h-80 mx-auto rounded-2xl shadow-xl" 
                />
              </div>
            )}
            
            {/* Host Peek Button - Only visible to host */}
            <div className="mt-8 mb-4">
              <button
                onClick={toggleHostPeek}
                className={`px-6 py-3 rounded-xl font-semibold text-base shadow-lg transition-all duration-300 transform hover:scale-105 ${
                  hostPeekAnswer 
                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700' 
                    : 'bg-gradient-to-r from-gray-600 to-gray-700 text-white hover:from-gray-500 hover:to-gray-600'
                }`}
              >
                {hostPeekAnswer ? '👁️ Hide Peek' : '👁️ Peek at Answer'}
              </button>
            </div>
            
            {/* Host Peek Answer - Only visible to host */}
            {hostPeekAnswer && (
              <div className="mt-4 p-4 bg-gradient-to-br from-blue-900 to-indigo-900 rounded-xl border-2 border-blue-700 shadow-lg">
                <h4 className="text-lg font-bold text-blue-300 mb-2">👁️ Host Peek - Answer</h4>
                <p className="text-lg font-semibold text-blue-200 mb-2">{qa.answer}</p>
                {qa.notes && (
                  <div className="mt-2 p-2 bg-blue-800/50 rounded border border-blue-600">
                    <p className="text-sm text-blue-100"><strong>Notes:</strong> {qa.notes}</p>
                  </div>
                )}
                {qa.answerImage && (
                  <div className="mt-2">
                    <img 
                      src={qa.answerImage} 
                      alt="Answer Visual" 
                      className="max-h-32 mx-auto rounded-lg shadow-md" 
                    />
                  </div>
                )}
              </div>
            )}
            
            {/* Audience Reveal Button */}
            <div className="mt-6 mb-6">
              <button
                onClick={toggleRevealAnswer}
                className={`px-8 py-4 rounded-2xl font-bold text-lg shadow-2xl transition-all duration-300 transform hover:scale-105 ${
                  revealAnswer 
                    ? 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700' 
                    : 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white hover:from-yellow-600 hover:to-orange-600'
                }`}
              >
                {revealAnswer ? '🔒 Hide from Audience' : '🔓 Reveal to Audience'}
              </button>
            </div>
            
            {/* Audience Reveal Answer */}
            {revealAnswer && (
              <div className="mt-8 p-6 bg-gradient-to-br from-green-900 to-emerald-900 rounded-2xl border-2 border-green-700 shadow-xl">
                <h4 className="text-2xl font-bold text-green-400 mb-4">🎯 Answer (Visible to Audience)</h4>
                <p className="text-xl font-semibold text-green-300 mb-4">{qa.answer}</p>
                {qa.answerImage && (
                  <div className="mt-4">
                    <img 
                      src={qa.answerImage} 
                      alt="Answer Visual" 
                      className="max-h-64 mx-auto rounded-2xl shadow-lg" 
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <HostLayout>
        <div className="min-h-screen bg-gray-900 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-24 w-24 border-4 border-blue-600 border-t-transparent mx-auto mb-8"></div>
            <h2 className="text-3xl font-bold text-white mb-2">Loading Game</h2>
            <p className="text-xl text-gray-400">Please wait...</p>
          </div>
        </div>
      </HostLayout>
    );
  }

  if (error) {
    return (
      <HostLayout>
        <div className="min-h-screen bg-gray-900 flex items-center justify-center">
          <div className="text-center">
            <div className="text-8xl mb-6">⚠️</div>
            <h2 className="text-3xl font-bold text-red-400 mb-4">{error}</h2>
            <button
              onClick={() => navigate('/home')}
              className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white px-8 py-4 rounded-2xl font-bold text-lg shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </HostLayout>
    );
  }


  // Render guard: only show UI when both session and game are ready
  const ready = sessionReadyRef.current && gameLoaded;

  if (!ready) {
    return (
      <HostErrorBoundary gameId={gameId}>
        <HostLayout hideNavigation={true}>
          <div className="min-h-screen bg-gray-900 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-white text-lg">Loading session & game…</p>
              <p className="text-gray-400 text-sm mt-2">
                Session: {sessionReadyRef.current ? '✅' : '⏳'} | Game: {gameLoaded ? '✅' : '⏳'}
              </p>
            </div>
          </div>
        </HostLayout>
      </HostErrorBoundary>
    );
  }

  // Hard-fail guard: if game is missing after loading
  if (!gameData) {
    return (
      <HostErrorBoundary gameId={gameId}>
        <HostLayout hideNavigation={true}>
          <div className="min-h-screen bg-gray-900 flex items-center justify-center">
            <div className="text-center">
              <p className="text-red-400 text-lg">Game not found or unavailable.</p>
              <p className="text-gray-400 text-sm mt-2">Game ID: {gameId}</p>
            </div>
          </div>
        </HostLayout>
      </HostErrorBoundary>
    );
  }

  return (
    <HostErrorBoundary gameId={gameId}>
      <HostLayout hideNavigation={true}>
        {/* Session Timer */}
        {showTimer && (
          <div className="fixed top-0 left-0 right-0 z-40 flex items-center justify-center py-2 text-sm bg-black/70 text-white">
            <span className="font-mono">Session time remaining: {hms}</span>
          </div>
        )}
        {/* Host Mode Protection System */}
        <StatusBanner
          saveStatus={protection.saveStatus}
          lastSaveTime={protection.lastSaveTime}
          isFullscreen={protection.isFullscreen}
          onToggleFullscreen={protection.toggleFullscreen}
          hasUnsavedChanges={protection.hasUnsavedChanges}
          gameId={gameId}
          onExitGame={handleExitGame}
        />
        
        {/* Protection Modals */}
        <ResumeGameModal
          isOpen={protection.showResumeModal}
          onResume={protection.handleResume}
          onDiscard={protection.handleDiscard}
          saveTime={protection.lastSaveTime}
          gameId={gameId}
        />
        
        <LeaveConfirmationModal
          isOpen={protection.showLeaveModal}
          onConfirm={protection.handleLeaveConfirm}
          onCancel={protection.handleLeaveCancel}
        />
        
        <SaveAndExitModal
          isOpen={protection.showSaveExitModal}
          onSaveAndExit={protection.handleSaveAndExit}
          onExitWithoutSaving={protection.handleExitWithoutSaving}
          onCancel={protection.handleCancelSaveExit}
          saveStatus={protection.saveStatus}
          gameId={gameId}
        />
        
        <div className="min-h-screen bg-gray-900">
        {/* Rating QR Code Modal */}
        {showRatingQR && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-xl p-6 max-w-md w-full">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-white">Rate Your Experience</h2>
                <button
                  onClick={closeRatingQR}
                  className="text-gray-400 hover:text-white"
                >
                  ✕
                </button>
              </div>
              <div className="text-center">
                <p className="text-gray-300 mb-4">
                  Scan this QR code to rate the host and game:
                </p>
                {qrCodeDataUrl ? (
                  <div className="bg-white p-4 rounded-lg inline-block">
                    <img 
                      src={qrCodeDataUrl} 
                      alt="Rating QR Code" 
                      className="w-64 h-64 mx-auto"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Teams can scan this to rate their experience
                    </p>
                  </div>
                ) : (
                  <div className="bg-white p-4 rounded-lg inline-block">
                    <div className="text-center text-gray-600">
                      Generating QR code...
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Submit Confirmation Modal */}
        {showSubmitConfirmation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-xl p-6 max-w-md w-full">
              <div className="text-center">
                <div className="text-6xl mb-4">⚠️</div>
                <h2 className="text-xl font-bold text-white mb-4">Final Confirmation</h2>
                <div className="bg-red-900/20 border border-red-600 rounded-lg p-4 mb-6">
                  <p className="text-red-300 text-sm font-semibold mb-2">IMPORTANT:</p>
                  <ul className="text-red-200 text-sm space-y-1">
                    <li>• Once submitted, data will be processed</li>
                    <li>• Access to this game will be permanently ended</li>
                    <li>• Game results cannot be modified</li>
                    <li>• Teams will be able to rate the experience</li>
                  </ul>
                </div>
                <div className="flex space-x-4">
                  <button
                    onClick={cancelSubmit}
                    className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-bold py-3 px-4 rounded transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmSubmit}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-4 rounded transition-colors"
                  >
                    Submit & End Game
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Game Expired Warning */}
        {/* {gameExpired && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-xl p-6 max-w-lg w-full">
              <div className="text-center">
                <div className="text-6xl mb-4">⏰</div>
                <h2 className="text-xl font-bold text-white mb-4">Game License Expired</h2>
                <div className="bg-yellow-900/20 border border-yellow-600 rounded-lg p-4 mb-6">
                  <p className="text-yellow-300 text-sm mb-3">
                    This purchased game has expired. To maintain a vibrant marketplace and encourage creators, 
                    purchased games expire 12 hours after first launch.
                  </p>
                  <div className="text-xs text-yellow-200 space-y-1">
                    <p>• This prevents unlimited reuse of purchased content</p>
                    <p>• Encourages new purchases and supports creators</p>
                    <p>• Keeps the marketplace active and fair</p>
                    <p>• Hosts can buy games in advance and have full usage time</p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => navigate('/marketplace')}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded transition-colors"
                  >
                    🛒 Browse Marketplace
                  </button>
                  <button
                    onClick={() => navigate('/my-games')}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded transition-colors"
                  >
                    📚 My Games
                  </button>
                </div>
              </div>
            </div>
          </div>
        )} */} {/* Removed gameExpired check */}

        {showTeamBreakdown && renderTeamBreakdown()}

        <div className="flex">
          {/* Main Content Area */}
          <div className="w-3/5 p-4">
            {/* Header with Stage Controls */}
            <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
              <div className="flex justify-between items-start">
                <div>
                  <h1 className="text-xl font-bold text-white">{getGameTitle(gameData)}</h1>
                  <p className="text-sm text-gray-400">Stage: {stageMap[currentStage]}</p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={launchAudienceDisplay}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm font-semibold transition-colors"
                  >
                    📺 Audience Display
                  </button>
                  <button
                    onClick={generateRatingQRCode}
                    className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg text-sm font-semibold transition-colors"
                  >
                    📱 QR
                  </button>
                  <button
                    onClick={protection.toggleFullscreen}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded-lg text-sm font-semibold transition-colors"
                    title={protection.isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                  >
                    ⛶ Full Screen Mode
                  </button>
                </div>
              </div>
              
              {/* Host Notes and Creator Comments */}
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Hosting Notes */}
                <div className="bg-gray-700 rounded-lg p-3 border border-gray-600">
                  <h3 className="text-sm font-semibold text-blue-300 mb-2 flex items-center">
                    📝 Hosting Notes
                  </h3>
                  <div className="text-xs text-gray-300 leading-relaxed">
                    {gameData.hostingNotes ? (
                      gameData.hostingNotes
                    ) : (
                      <span className="text-gray-500 italic">
                        No hosting notes available for this game.
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Creator Comment for Current Question */}
                <div className="bg-gray-700 rounded-lg p-3 border border-gray-600">
                  <h3 className="text-sm font-semibold text-green-300 mb-2 flex items-center">
                    💡 Creator Comment
                  </h3>
                  <div className="text-xs text-gray-300 leading-relaxed">
                    {(() => {
                      const currentQuarter = getCurrentQuarter(currentStage);
                      let creatorComment = '';
                      
                      if (typeof currentQuarter === 'number' && currentQuarter >= 1 && currentQuarter <= 4) {
                        // For quarter questions
                        const questionIndex = getQuestionIndex(currentStage, currentQuarter);
                        const quarterData = gameData[`quarter${currentQuarter}`];
                        if (quarterData && quarterData[questionIndex]) {
                          creatorComment = quarterData[questionIndex].creatorComment || '';
                        }
                      } else if (typeof currentQuarter === 'string') {
                        // For special sections (halftime, final, tieBreaker)
                        const sectionData = gameData[currentQuarter];
                        if (sectionData) {
                          creatorComment = sectionData.creatorComment || '';
                        }
                      }
                      
                      return creatorComment ? (
                        creatorComment
                      ) : (
                        <span className="text-gray-500 italic">
                          No creator comment for this question.
                        </span>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>

            {/* Countdown Timer for Purchased Games */}
            {/* {timeRemaining && (
              <div className={`mt-3 text-white px-4 py-3 rounded-lg shadow-lg border ${
                timeRemaining.hours === 0 
                  ? 'bg-red-600 border-red-500' 
                  : timeRemaining.hours <= 1 
                    ? 'bg-orange-600 border-orange-500' 
                    : 'bg-yellow-600 border-yellow-500'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">
                      {timeRemaining.hours === 0 ? '🚨' : timeRemaining.hours <= 1 ? '⚠️' : '⏰'}
                    </span>
                    <div>
                      <div className="text-sm font-medium">
                        {timeRemaining.hours === 0 
                          ? 'Game Expires Soon!' 
                          : timeRemaining.hours <= 1 
                            ? 'Game Expires In' 
                            : 'Game Expires In'
                        }
                      </div>
                      <div className="text-xl font-bold">
                        {timeRemaining.hours}h {timeRemaining.minutes}m
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs opacity-80">Game expires 12 hours after first launch</div>
                    <div className="text-xs opacity-80">to encourage marketplace activity</div>
                    {timeRemaining.hours === 0 && (
                      <div className="text-xs font-semibold mt-1">
                        ⚠️ Complete your game soon!
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )} */} {/* Removed timeRemaining check */}

            {/* Progress Indicator */}
            {gameData && (
              <div className="mt-3 bg-gray-700 rounded-lg p-3 border border-gray-600">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="text-center">
                      <div className="text-sm font-bold text-white">
                        {getProgressInfo(currentStage).quarter}
                      </div>
                      <div className="text-xs text-gray-400">Current</div>
                    </div>
                    {getProgressInfo(currentStage).question && typeof getProgressInfo(currentStage).question === 'number' && (
                      <div className="text-center">
                        <div className="text-sm font-bold text-blue-400">
                          Q{getProgressInfo(currentStage).question}
                        </div>
                        <div className="text-xs text-gray-400">Question</div>
                      </div>
                    )}
                    {getProgressInfo(currentStage).totalQuestions && (
                      <div className="text-center">
                        <div className="text-sm font-bold text-green-400">
                          {getProgressInfo(currentStage).question}/{getProgressInfo(currentStage).totalQuestions}
                        </div>
                        <div className="text-xs text-gray-400">Progress</div>
                      </div>
                    )}
                  </div>
                  
                  {/* Upcoming Categories */}
                  <div className="flex-1 ml-4">
                    <div className="text-xs font-semibold text-gray-300 mb-1">Upcoming:</div>
                    <div className="flex flex-wrap gap-1">
                      {getUpcomingCategories(currentStage, gameData).map((category, index) => (
                        <span 
                          key={index}
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            index === 0 
                              ? 'bg-yellow-600 text-white' 
                              : 'bg-gray-600 text-gray-300'
                          }`}
                        >
                          {category}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Stage Content */}
            <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
              {renderStageContent()}
              {/* Navigation Controls */}
              <div className="mt-8 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex gap-4">
                  <button
                    onClick={isSlideStage(currentStage) ? previousSlide : () => handleStageChange(Math.max(1, currentStage - 1))}
                    className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-xl font-semibold transition-colors disabled:opacity-50"
                    disabled={currentStage === 1 && currentSlideIndex === 0}
                  >
                    ⬅️ {isSlideStage(currentStage) ? 'Previous Slide' : 'Previous'}
                  </button>
                  <button
                    onClick={isSlideStage(currentStage) ? nextSlide : () => handleStageChange(getNextStage(currentStage))}
                    className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-xl font-semibold transition-colors disabled:opacity-50"
                    disabled={currentStage === 40}
                  >
                    {isSlideStage(currentStage) ? 'Next Slide' : 'Next'} ➡️
                  </button>
                </div>
                <div className="flex items-center gap-4">
                  {isSlideStage(currentStage) && (
                    <div className="text-gray-300 text-sm">
                      Slide {currentSlideIndex + 1} of {getSlidesForCurrentStage.length}
                    </div>
                  )}
                  <div>
                    <label htmlFor="stage-jump" className="text-gray-300 mr-2">Jump to Stage:</label>
                    <select
                      id="stage-jump"
                      value={currentStage}
                      onChange={e => handleStageChange(Number(e.target.value))}
                      className="bg-gray-900 text-white border border-gray-600 rounded px-4 py-2"
                    >
                      {Object.entries(stageMap).map(([num, label]) => (
                        <option key={num} value={num}>{num}. {label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Team Manager Sidebar */}
          <div className="w-2/5 p-4">
            <div className="bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-700 h-full overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold bg-gradient-to-r from-green-400 to-blue-400 bg-clip-text text-transparent">
                  {currentStage === 38 ? 'Tiebreaker Teams' : 'Teams'}
                </h3>
                {currentStage !== 38 && (
                  <button 
                    onClick={addTeam} 
                    className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-4 py-2 rounded-lg text-base font-semibold shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105"
                  >
                    + Add Team
                  </button>
                )}
              </div>
              
              {/* Debug info for tiebreaker */}
              {currentStage === 38 && (
                <div className="mb-4 p-2 bg-red-900/20 border border-red-600 rounded text-xs text-red-300">
                  <div>Debug: Current Stage: {currentStage}</div>
                  <div>Debug: Tied Teams Count: {tiedTeams.length}</div>
                  <div>Debug: All Teams Count: {teams.length}</div>
                  <div>Debug: Tied Teams: {tiedTeams.map(t => t.name).join(', ')}</div>
                  <div>Debug: Tie Groups: {tieGroups.map(g => `${g.position}(${g.teams.map(t => t.name).join(',')})`).join(' | ')}</div>
                  <div className="mt-2">
                    <button 
                      onClick={() => {
                        console.log('🔧 Manual tie check triggered');
                        checkForTiesAndAdvance();
                      }}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded text-xs"
                    >
                      Manual Check Ties
                    </button>
                  </div>
                </div>
              )}
              {(currentStage === 38 ? teams.filter(team => tiedTeams.some(tiedTeam => tiedTeam.id === team.id)) : teams).length === 0 ? (
                <div className="text-center py-6">
                  <div className="text-2xl mb-2">👥</div>
                  <p className="text-gray-400 font-medium text-base">
                    {currentStage === 38 ? 'No tied teams' : 'No teams'}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Tiebreaker indicator */}
                  {currentStage === 38 && (
                    <div className="space-y-3 mb-4">
                      <div className="bg-gradient-to-r from-yellow-900 to-orange-900 p-3 rounded-lg border border-yellow-600">
                        <div className="flex items-center justify-center space-x-2 mb-2">
                          <span className="text-yellow-400 text-lg">⚡</span>
                          <span className="text-yellow-200 font-semibold">
                            Tiebreaker: {tiedTeams.length} tied teams
                          </span>
                          <span className="text-yellow-400 text-lg">⚡</span>
                        </div>
                        <div className="text-center text-yellow-100 text-sm">
                          Score the tiebreaker question to determine final rankings
                        </div>
                      </div>
                      
                      {/* Show tie groups */}
                      {tieGroups.map((group, index) => (
                        <div key={index} className="bg-gradient-to-r from-blue-900 to-purple-900 p-2 rounded-lg border border-blue-600">
                          <div className="text-center">
                            <div className="text-blue-200 font-semibold text-sm">
                              Tie for {group.position} Place ({group.score} pts)
                            </div>
                            <div className="text-blue-100 text-xs mt-1">
                              {group.teams.map(team => team.name).join(' • ')}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Show only tied teams during tiebreaker, all teams otherwise */}
                  {(currentStage === 38 ? teams.filter(team => tiedTeams.some(tiedTeam => tiedTeam.id === team.id)) : teams).map((team, index) => (
                    <div key={team.id} className="bg-gradient-to-br from-gray-700 to-gray-800 p-1 rounded shadow border border-gray-600 hover:shadow-lg transition-colors duration-200">
                      <div className="flex items-center justify-between mb-0.5">
                        <div className="flex-1 min-w-0">
                          <button
                            onClick={() => showTeamBreakdownModal(team)}
                            className="text-sm font-bold bg-transparent border-b border-gray-500 hover:border-blue-400 focus:border-blue-400 focus:outline-none transition-colors text-left cursor-pointer hover:text-blue-400 text-white w-full overflow-hidden text-ellipsis whitespace-nowrap"
                          >
                            {team.name}
                          </button>
                        </div>
                        <div className="text-sm font-bold text-white mr-0.5 ml-2 flex-shrink-0">
                          {team.score}
                        </div>
                        <button 
                          onClick={() => deleteTeam(team.id)} 
                          className="text-red-400 hover:text-red-600 text-base font-bold transition-colors px-2 flex-shrink-0"
                        >
                          ✕
                        </button>
                      </div>
                      {/* Regular Question Scoring (1-5 points) */}
                      {isScoringEnabled(currentStage) && currentQuarter !== 'halftime' && currentQuarter !== 'final' && (
                        <>
                          {/* Green buttons row */}
                          <div className="flex flex-row gap-0.5 mb-0.5">
                            {[1,2,3,4,5].map((val) => {
                              const isUsed = team.usedPoints?.[currentQuarter]?.some(entry => typeof entry === 'number' ? entry === val : entry.points === val);
                              const isWrong = team.wrongAnswers?.[currentQuarter]?.some(entry => typeof entry === 'number' ? entry === val : entry.points === val);
                              const hasAnswered = teamsAnsweredCurrentQuestion.has(team.id);
                              return (
                                <button
                                  key={val}
                                  onClick={() => handleScore(team.id, val)}
                                  className={`bg-green-600 hover:bg-green-700 text-white font-bold py-0 px-1.5 rounded text-sm shadow transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-green-400 ${isUsed || isWrong ? 'opacity-50 cursor-not-allowed' : ''} ${hasAnswered ? 'opacity-30' : ''}`}
                                  disabled={isUsed || isWrong}
                                >
                                  +{val}
                                </button>
                              );
                            })}
                          </div>
                          {/* Red X buttons row */}
                          <div className="flex flex-row gap-0.5 mb-0.5">
                            {[1,2,3,4,5].map((val) => {
                              const isUsed = team.usedPoints?.[currentQuarter]?.some(entry => typeof entry === 'number' ? entry === val : entry.points === val);
                              const isWrong = team.wrongAnswers?.[currentQuarter]?.some(entry => typeof entry === 'number' ? entry === val : entry.points === val);
                              const hasAnswered = teamsAnsweredCurrentQuestion.has(team.id);
                              return (
                                <button
                                  key={val}
                                  onClick={() => handleWrongAnswer(team.id, val)}
                                  className={`bg-red-600 hover:bg-red-700 text-white font-bold py-0 px-1.5 rounded text-sm shadow transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-red-400 ${isUsed || isWrong ? 'opacity-50 cursor-not-allowed' : ''} ${hasAnswered ? 'opacity-30' : ''}`}
                                  disabled={isUsed || isWrong}
                                >
                                  X
                                </button>
                              );
                            })}
                          </div>
                          {/* Undo buttons row */}
                          <div className="flex flex-row gap-0.5 mb-0.5">
                            {[1,2,3,4,5].map((val) => (
                              <button
                                key={val}
                                onClick={() => handleUndo(team.id, val)}
                                className="bg-yellow-400 hover:bg-yellow-500 text-white rounded font-bold text-xs shadow-sm transition-all duration-200 transform hover:scale-105 py-0 px-1.5"
                              >
                                ↺
                              </button>
                            ))}
                          </div>
                        </>
                      )}

                      {/* Halftime Manual Scoring */}
                      {currentQuarter === 'halftime' && (
                        <div className={`flex items-center gap-2 mt-2 p-2 border rounded ${teamsAnsweredCurrentQuestion.has(team.id) ? 'bg-blue-900/10 border-blue-400/50 opacity-50' : 'bg-blue-900/20 border-blue-600'}`}>
                          <div className="text-xs text-blue-300 font-semibold">Halftime:</div>
                          <input
                            type="number"
                            value={team.halftimeScore || ''}
                            onChange={(e) => handleHalftimeScore(team.id, e.target.value)}
                            className={`w-16 border rounded px-2 text-sm font-semibold focus:outline-none transition-colors bg-gray-900 text-white ${teamsAnsweredCurrentQuestion.has(team.id) ? 'border-blue-400/50 opacity-50' : 'border-blue-500 focus:border-blue-400'}`}
                            placeholder="0"
                            min="0"
                            disabled={teamsAnsweredCurrentQuestion.has(team.id)}
                          />
                          {!teamsAnsweredCurrentQuestion.has(team.id) && (
                            <button
                              onClick={() => submitIndividualHalftimeScore(team.id)}
                              className="w-6 h-6 bg-green-500 hover:bg-green-600 text-white rounded text-xs font-semibold shadow-sm transition-all duration-200 transform hover:scale-105"
                              title="Submit Halftime Score"
                            >
                              ✓
                            </button>
                          )}
                          {team.correctAnswers?.['halftime']?.length > 0 && (
                            <button
                              onClick={() => undoHalftimeScores()}
                              className="w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded text-xs font-semibold shadow-sm transition-all duration-200 transform hover:scale-105"
                              title="Undo Halftime Scores"
                            >
                              ↺
                            </button>
                          )}

                        </div>
                      )}

                      {/* Final Manual Scoring */}
                      {currentQuarter === 'final' && (
                        <div className={`flex items-center gap-2 mt-2 p-2 border rounded ${teamsAnsweredCurrentQuestion.has(team.id) ? 'bg-purple-900/10 border-purple-400/50 opacity-50' : 'bg-purple-900/20 border-purple-600'}`}>
                          <div className="text-xs text-purple-300 font-semibold">Final:</div>
                          <input
                            type="number"
                            value={team.finalScore || ''}
                            onChange={(e) => handleFinalScore(team.id, e.target.value)}
                            className={`w-16 border rounded px-2 text-sm font-semibold focus:outline-none transition-colors bg-gray-900 text-white ${teamsAnsweredCurrentQuestion.has(team.id) ? 'border-purple-400/50 opacity-50' : 'border-purple-500 focus:border-purple-400'}`}
                            placeholder="0"
                            disabled={teamsAnsweredCurrentQuestion.has(team.id)}
                          />
                          {!teamsAnsweredCurrentQuestion.has(team.id) && (
                            <button
                              onClick={() => submitIndividualFinalScore(team.id)}
                              className="w-6 h-6 bg-green-500 hover:bg-green-600 text-white rounded text-xs font-semibold shadow-sm transition-all duration-200 transform hover:scale-105"
                              title="Submit Final Score"
                            >
                              ✓
                            </button>
                          )}
                          {team.correctAnswers?.['final']?.length > 0 && (
                            <button
                              onClick={() => undoFinalScores()}
                              className="w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded text-xs font-semibold shadow-sm transition-all duration-200 transform hover:scale-105"
                              title="Undo Final Scores"
                            >
                              ↺
                            </button>
                          )}

                        </div>
                      )}

                      {/* Tiebreaker Checkboxes - Only show for tied teams */}
                      {currentStage === 38 && tiedTeams.some(tiedTeam => tiedTeam.id === team.id) && (
                        <div className="flex items-center gap-2 mt-2 p-2 bg-yellow-900/20 border border-yellow-600 rounded">
                          <div className="text-xs text-yellow-300 font-semibold">Tiebreaker:</div>
                          <input
                            type="checkbox"
                            checked={team.tiebreakerWinner || false}
                            onChange={(e) => handleTiebreakerScore(team.id, e.target.checked)}
                            className="w-4 h-4 text-yellow-600 bg-gray-900 border-yellow-500 rounded focus:ring-yellow-500 focus:ring-2"
                          />
                          <span className="text-xs text-yellow-200">Winner</span>
                          {team.tiebreakerWinner && (
                            <button
                              onClick={() => undoTiebreakerScores()}
                              className="w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded text-xs font-semibold shadow-sm transition-all duration-200 transform hover:scale-105"
                              title="Undo Tiebreaker Selection"
                            >
                              ↺
                            </button>
                          )}
                        </div>
                      )}

                    </div>
                  ))}
                </div>
              )}
              {/* Auto-save indicator */}
              <div className="mt-4 p-2 bg-gradient-to-r from-green-900 to-emerald-900 rounded border border-green-700">
                <div className="flex items-center justify-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Team Search Modal */}
      {showTeamSearchModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto border border-gray-700">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">Add Team</h2>
              <button
                onClick={() => {
                  setShowTeamSearchModal(false);
                  setTeamSearchQuery('');
                  setFilteredLeagueTeams([]);
                }}
                className="text-gray-400 hover:text-white text-2xl"
              >
                ×
              </button>
            </div>

            {/* Search Tabs */}
            <div className="flex space-x-4 mb-6">
              <button
                onClick={(e) => addManualTeam(e)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
              >
                📝 Manual Entry
              </button>
              <button
                onClick={() => setTeamSearchQuery('')}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
              >
                🔍 League Search
              </button>
            </div>

            {/* Search Input */}
            <div className="mb-6">
              <input
                type="text"
                value={teamSearchQuery}
                onChange={(e) => {
                  setTeamSearchQuery(e.target.value);
                  searchLeagueTeams(e.target.value);
                }}
                placeholder="Search by team name, league ID, or venue..."
                className="w-full p-4 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-green-500 focus:outline-none text-lg"
              />
            </div>

            {/* Recent & Frequent Teams - Teams that play with this host often */}
            {!teamSearchQuery && recentTeams.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-white mb-3">
                  Your Regular Teams ({recentTeams.length})
                </h3>
                <p className="text-sm text-gray-400 mb-3">
                  Teams you've hosted games with recently
                </p>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {recentTeams.map((team) => (
                    <div
                      key={team.id || team.leagueId}
                      onClick={() => addLeagueTeam(team)}
                      className="flex items-center justify-between p-3 bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-600 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <div className="text-white font-semibold">{team.name}</div>
                          {team.frequency > 1 && (
                            <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded">
                              {team.frequency}x
                            </span>
                          )}
                        </div>
                        <div className="text-gray-400 text-sm">{team.leagueId} • {team.homeVenue}</div>
                        {team.lastPlayed && (
                          <div className="text-gray-500 text-xs mt-1">
                            Last played: {team.lastPlayed.toLocaleDateString()}
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-green-400 font-semibold">GPA: {team.gpa.toFixed(1)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Search Results */}
            {teamSearchQuery && filteredLeagueTeams.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Search Results</h3>
                <div className="space-y-2">
                  {filteredLeagueTeams.map((team) => (
                    <div
                      key={team.id}
                      onClick={() => addLeagueTeam(team)}
                      className="flex items-center justify-between p-3 bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-600 transition-colors"
                    >
                      <div>
                        <div className="text-white font-semibold">{team.name}</div>
                        <div className="text-gray-400 text-sm">{team.leagueId} • {team.homeVenue}</div>
                      </div>
                      <div className="text-green-400 font-semibold">GPA: {team.gpa}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* No Results */}
            {teamSearchQuery && filteredLeagueTeams.length === 0 && (
              <div className="text-center py-8">
                <div className="text-4xl mb-4">🔍</div>
                <p className="text-gray-400">No teams found matching "{teamSearchQuery}"</p>
                <p className="text-gray-500 text-sm mt-2">Try searching by team name, league ID, or venue</p>
              </div>
            )}


          </div>
        </div>
      )}

      {/* 12-Hour Launch Warning Modal */}
      {showLaunchWarning && launchSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 w-full max-w-md mx-4 border border-gray-200">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
                <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">12-Hour Launch Window Started</h3>
              <p className="text-sm text-gray-500 mb-4">
                Your 12-hour rental window for <strong>{launchSession.gameTitle || 'this game'}</strong> has begun.
                This window cannot be paused or extended.
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-6">
                <p className="text-sm text-yellow-800">
                  ⏰ <strong>Important:</strong> You have exactly 12 hours to complete your game session. 
                  The timer cannot be paused or transferred.
                </p>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    localStorage.setItem(`launchWarnAck_${launchSession.sessionId}`, '1');
                    setShowLaunchWarning(false);
                  }}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  I Understand
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {showMobileRestriction && (
        <MobileRestriction 
          feature="game_launch"
          onClose={() => {
            setShowMobileRestriction(false);
            navigate('/my-games');
          }}
        />
      )}

      {/* Manual Entry Modal - Custom modal to preserve fullscreen */}
      {showManualEntryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md border border-gray-700 shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-white">Enter Team Name</h2>
              <button
                onClick={() => {
                  setShowManualEntryModal(false);
                  setManualTeamName('');
                }}
                className="text-gray-400 hover:text-white text-2xl transition-colors"
              >
                ×
              </button>
            </div>
            
            <div className="mb-4">
              <input
                type="text"
                value={manualTeamName}
                onChange={(e) => {
                  setManualTeamName(e.target.value);
                  // Check and restore fullscreen if lost during typing
                  if (wasFullscreenBeforeModalRef.current && !document.fullscreenElement) {
                    document.documentElement.requestFullscreen().catch(() => {});
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleManualTeamSubmit();
                  } else if (e.key === 'Escape') {
                    setShowManualEntryModal(false);
                    setManualTeamName('');
                  }
                }}
                onFocus={(e) => {
                  // Restore fullscreen when input is focused (browser might exit it)
                  if (wasFullscreenBeforeModalRef.current && !document.fullscreenElement) {
                    // Use setTimeout to let browser finish its focus operation first
                    setTimeout(() => {
                      document.documentElement.requestFullscreen().catch(() => {});
                    }, 100);
                  }
                }}
                placeholder="Team name..."
                autoFocus
                className="w-full p-4 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-green-500 focus:outline-none text-lg"
              />
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={handleManualTeamSubmit}
                disabled={!manualTeamName.trim()}
                className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-lg transition-colors"
              >
                Add Team
              </button>
              <button
                onClick={() => {
                  setShowManualEntryModal(false);
                  setManualTeamName('');
                }}
                className="flex-1 bg-gray-600 hover:bg-gray-500 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      </HostLayout>
    </HostErrorBoundary>
  );
};

export default LaunchGame;